import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Tabs, Tag, Button, Descriptions, Spin, Table, Empty, Alert, Progress,
  Space, Popconfirm, Tooltip, Dropdown, Modal, Upload, Form, Divider,
} from 'antd';
import {
  ArrowLeftOutlined, FileTextOutlined, UserOutlined, MailOutlined, DollarOutlined, AuditOutlined,
  FolderOpenOutlined, DownloadOutlined, UserSwitchOutlined, FileExcelOutlined,
  ToolOutlined,
  WalletOutlined,
  SafetyCertificateOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined,
  UnlockOutlined,
  PrinterOutlined,
  StopOutlined,
  CheckCircleOutlined,
  FileDoneOutlined,
  ClockCircleOutlined,
  ProfileOutlined, CarOutlined, GlobalOutlined, ArrowRightOutlined,
  MedicineBoxOutlined, TeamOutlined,
  EyeOutlined, BankOutlined, RollbackOutlined, CloseCircleOutlined,
  SmileOutlined, CheckOutlined, FolderOutlined, WarningOutlined,
  PaperClipOutlined, FireOutlined, SwapOutlined, MobileOutlined,
  CommentOutlined, LinkOutlined, SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import NouveauPvModal from '../pv-sinistres/NouveauPvModal';
import DetailPvModal from '../pv-sinistres/DetailPvModal';
import { NumeroPvAffiche } from '../pv-sinistres/numeroPv';
import VictimeModal from '../../components/VictimeModal';
import AdversaireModal from '../../components/AdversaireModal';
import NegociationRcPanel from '../../components/NegociationRcPanel';
import CBModal from '../../components/CBModal';
import { telechargerModeleVictimes, lireVictimesExcel } from './declaration/importVictimesExcel';
import ContentieuxModal from '../../components/ContentieuxModal';
import ArbitrageModal from '../../components/ArbitrageModal';
import ValidationModal from '../../components/ValidationModal';
import FicheHomologueModal from '../../components/FicheHomologueModal';
import DemandeGarantieBandeau from '../../components/DemandeGarantieBandeau';
import ConsolidationModal from '../../components/ConsolidationModal';
import RcAssureModal from '../../components/RcAssureModal';
import EchangesPartenairesPanel from '../../components/EchangesPartenairesPanel';
import { partieGarantie, destinataireGarantie } from '../../services/partieGarantie';
import {
  estSinistreTe, ficheHomologueParmi, ficheHomologueAttendue, joindrePiecesHomologue,
} from '../../services/ficheHomologueService';
import ConfirmationGarantieModal from '../../components/ConfirmationGarantieModal';
import DemandePvModal from '../../components/DemandePvModal';
import RejeterGarantieModal from '../../components/RejeterGarantieModal';
import Layout from '../../components/Layout';
import BandeauAttentes from './components/BandeauAttentes';
import DiscussionDossier from '../../components/discussion/DiscussionDossier';
import PastilleDiscussion from '../../components/discussion/PastilleDiscussion';
import { ENTITE, ANCRE } from '../../services/discussionService';
import axiosInstance from '../../variables/api/axiosInstance';
import {
  formatDate, formatMontant, STATUT_MAP, STATUT_DOSSIER_MAP, isDossierCloture, splitCategoriesPermis,
  libelleStatutGarantie, HAB, can, estCanalMobile, estAdversaireBlesse, estVictimeIndemnisable,
  TYPES_ADVERSAIRE, STATUT_VICTIME_MAP, POSITION_RC_MAP, CANAL_MAP,
} from '../../variables/constants';
import ChampResponsabilite, {
  libelleResponsabilite, responsabiliteDepuisTaux, tauxRcDepuisSaisie,
} from '../../components/ChampResponsabilite';
import { toast } from 'react-toastify';
import OngletExpertises from './components/OngletExpertises';
import BilanVictimesCard from './components/BilanVictimesCard';
import { memePersonneAutreRole, confirmerDoublonPersonne } from './doublonPersonne';
import GedUploader from '../../components/GedUploader';
import SinistreStatusStepper from '../../components/SinistreStatusStepper';
import OngletReglements from './components/OngletReglements';
import OngletOffresIndemnisation from './components/OngletOffresIndemnisation';
import DocumentPreviewModal from './components/DocumentPreviewModal';
import ChangerStatutModal from './components/ChangerStatutModal';
import PanneauLiaison from './components/PanneauLiaison';
import OngletEncaissements from './components/OngletEncaissements';
import OngletPrefinancements from './components/OngletPrefinancements';
import OngletDelaisPenalites from './components/OngletDelaisPenalites';
import OngletDocuments from './components/OngletDocuments';
import OngletBilateraux from './components/OngletBilateraux';
import PanneauAffectation from './components/PanneauAffectation';
import TagRc from '../../components/TagRc';
import ResumeEntete from './components/ResumeEntete';
import { RubanNonAffecte } from './components/NonAffecte';
import OngletDeclarationMobile from './components/mobile/OngletDeclarationMobile';
import FilDiscussionMobile from './components/mobile/FilDiscussionMobile';
import mobileService from '../../services/mobileService';

import i18n from '../../i18n';

/**
 * Regroupement des onglets du detail sinistre.
 *
 * Seize onglets dans une barre a defilement horizontal : pour atteindre
 * « Reglements », un agent devait faire defiler une barre ou rien n'indique ce qui
 * se trouve plus loin. On cherche « ou est l'argent », pas « ou est l'onglet
 * Prefinancements ».
 *
 * Cinq groupes, et la cle de l'onglet FEUILLE reste la cle d'URL. Les liens profonds
 * `?tab=encaissements` continuent donc de fonctionner a l'identique, tout comme les
 * `setActiveTab('delais')` poses par les bandeaux d'alerte : le groupe s'ouvre tout
 * seul puisqu'il se deduit de la feuille active.
 *
 * Un groupe dont aucun onglet n'est monte (droits, canal) n'est pas rendu : mieux
 * vaut cinq groupes dont un manque qu'un groupe vide qui promet quelque chose.
 */
const GROUPES_ONGLETS = [
  { cle: 'dossier', libelle: 'details.groupes.leDossier', onglets: ['infos', 'victimes', 'pv', 'declaration-mobile', 'liaison'] },
  { cle: 'instruction', libelle: 'details.groupes.instruction', onglets: ['expertises', 'delais', 'echanges-usager'] },
  // Cle interne 'argent' conservee a dessein : le groupe s'affiche « Règlement »,
  // mais il contient deja un onglet FEUILLE de cle 'reglements'. Nommer les deux
  // pareil rendrait illisible tout code qui manipule l'un ou l'autre. La cle de
  // groupe ne sort jamais dans l'URL, elle n'a donc pas a etre parlante.
  // « Reglements » en tete : c'est l'acte qu'on vient poser dans ce groupe. Les
  // offres, encaissements et prefinancements sont ce qui le CONDITIONNE - on les
  // consulte quand le reglement coince, pas avant.
  { cle: 'argent', libelle: 'details.groupes.reglement', onglets: ['reglements', 'offres', 'encaissements', 'prefinancements'] },
  // 'echanges' (Échanges partenaires) : sans groupe, l'onglet déclaré n'était jamais rendu.
  { cle: 'pieces', libelle: 'details.groupes.piecesCourriers', onglets: ['documents', 'courriers', 'echanges'] },
  { cle: 'suivi', libelle: 'details.groupes.suivi', onglets: ['affectation', 'bilateraux', 'journal'] },
];

/**
 * Construit les onglets de premier niveau a partir de la liste plate.
 *
 * @param plats     les seize onglets tels qu'ils sont declares aujourd'hui
 * @param actif     cle de l'onglet FEUILLE courant (= la cle d'URL)
 * @param onChange  appele avec la cle d'une feuille
 * @param t         traduction de l'espace sinistres (libelles des groupes)
 */
const grouperOnglets = (plats, actif, onChange, t) => {
  const parCle = new Map(plats.filter(Boolean).map((o) => [o.key, o]));
  return GROUPES_ONGLETS
    .map((g) => {
      const contenu = g.onglets.map((c) => parCle.get(c)).filter(Boolean);
      if (!contenu.length) return null;
      return {
        key: g.cle,
        label: t(g.libelle),
        children: (
          <Tabs
            activeKey={contenu.some((o) => o.key === actif) ? actif : contenu[0].key}
            onChange={onChange}
            tabBarStyle={{ padding: '0 24px', margin: 0, borderBottom: '1px solid #f1f3f6' }}
            items={contenu}
          />
        ),
      };
    })
    .filter(Boolean);
};

/** Groupe contenant une feuille donnee ; le premier groupe a defaut. */
const groupeDeLOnglet = (cleFeuille) =>
  GROUPES_ONGLETS.find((g) => g.onglets.includes(cleFeuille))?.cle ?? GROUPES_ONGLETS[0].cle;

const getCurrentDossier = (dossiers = []) => [...dossiers].sort((a, b) => {
  if (Boolean(a?.estMur) !== Boolean(b?.estMur)) return a?.estMur ? -1 : 1;
  return new Date(b?.dateOuverture || 0).getTime() - new Date(a?.dateOuverture || 0).getTime();
})[0] || null;

/* `heureAccident` arrive en « HH:mm:ss » (LocalTime sérialisé), pas en date :
   `dayjs(t)` le rendrait invalide. Même découpe que FicheDeclaration#fmtTime. */
const formatHeure = (t) => {
  if (!t) return '';
  const [hh, mm] = String(t).split(':');
  return i18n.t('sinistres:details.infos.heure', { hh, mm: mm || '00' });
};

/*  Onglet Informations générales  */
const PuceImmatriculation = ({ valeur }) => (valeur
  ? <span style={{ fontFamily: 'var(--font-main)', background: '#f4f6f5', padding: '1px 6px', borderRadius: 4 }}>{valeur}</span>
  : '-');

/* Nom d'une partie adverse. Les fiches anciennes portent « ADVERSAIRE » / « - »
   comme nom de remplissage : on montre alors ce qu'elle est (piéton, véhicule…). */
const nomPartieAdverse = (a) => {
  const nom = [a.nom, a.prenoms]
    .filter((x) => x && !['ADVERSAIRE', '-', '-'].includes(String(x).trim().toUpperCase()))
    .join(' ');
  if (nom) return nom;
  const type = TYPES_ADVERSAIRE.find((t) => t.value === (a.typeAdversaire || 'ENGINS'));
  return a.typeAdversaire === 'AUTRE' && a.typeAdversaireAutre ? a.typeAdversaireAutre : (type?.label || i18n.t('sinistres:details.infos.partieAdverse'));
};

const InfoGenerales = ({ sinistre, victimes = [], pv = null }) => {
  const { t } = useTranslation('sinistres');
  const adversaires = victimes.filter((v) => v.estAdversaire);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* Infos sinistre */}
      <div className="cb-form-section">
        <div className="cb-form-section-title"><ProfileOutlined /> {t('details.infos.donneesSinistre')}</div>
        <Descriptions column={1} size="small" labelStyle={{ color: '#6b7280', width: 160 }}>
          <Descriptions.Item label={t('details.infos.numeroLocal')}>{sinistre.numeroSinistreLocal}</Descriptions.Item>
          <Descriptions.Item label={t('details.infos.numeroHomologue')}>{sinistre.numeroSinistreHomologue || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('details.infos.typeSinistre')}>
            <Tag color={sinistre.typeSinistre === 'SURVENU_TOGO' ? 'geekblue' : 'purple'} style={{ borderRadius: 20 }}>
              {sinistre.typeSinistre === 'SURVENU_TOGO' ? t('details.infos.survenuTogo') : t('details.infos.survenuEtranger')}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('details.infos.typeDommage')}>
            <Tag color={sinistre.typeDommage === 'CORPOREL' ? 'red' : 'orange'} style={{ borderRadius: 20 }}>
              {sinistre.typeDommage}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('details.infos.statut')}>
            {STATUT_MAP[sinistre.statut] && (
              <Tag color={STATUT_MAP[sinistre.statut].color} style={{ borderRadius: 20 }}>
                {libelleStatutGarantie(sinistre.statut, sinistre.typeSinistre)}
              </Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('details.infos.dateAccident')}>
            {formatDate(sinistre.dateAccident)}
            {sinistre.heureAccident ? ` · ${formatHeure(sinistre.heureAccident)}` : ''}
          </Descriptions.Item>
          <Descriptions.Item label={t('details.infos.dateDeclaration')}>{formatDate(sinistre.dateDeclaration)}</Descriptions.Item>
          {/* Le lieu de l'accident se lisait ici sur la seule ligne « Lieu », alors que
            la déclaration en saisit le détail : ville, commune, lieu précis et trajet.
            Tout était en base et dans la réponse de l'API, rien n'était montré - un
            agent devait rouvrir le formulaire pour savoir où l'accident avait eu lieu.
            Même découpage que le récapitulatif de la déclaration, pour que l'écran de
            consultation dise exactement ce que l'écran de saisie a demandé. */}
          <Descriptions.Item label={t('details.infos.villeCommune')}>
            {[sinistre.ville, sinistre.commune].filter(Boolean).join(' - ') || '-'}
          </Descriptions.Item>
          {/* Le PV fait foi sur le lieu : quand il diffère de ce qui a été déclaré, on
            montre les deux plutôt que d'en effacer un - c'est l'écart qui informe. */}
          <Descriptions.Item label={t('details.infos.lieuPrecis')}>
            {sinistre.lieuAccident || '-'}
            {pv?.lieuAccident && pv.lieuAccident.trim().toLowerCase() !== (sinistre.lieuAccident || '').trim().toLowerCase() && (
              <div style={{ fontSize: 12.5, color: '#b45309', marginTop: 2 }}>
                {t('details.infos.dApresPv', { lieu: pv.lieuAccident })}
              </div>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('details.infos.trajet')}>
            {[sinistre.provenance, sinistre.destination].filter(Boolean).join(t('details.infos.vers')) || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('details.infos.agglomeration')}>
            {sinistre.agglomeration === true ? t('details.infos.oui')
              : sinistre.agglomeration === false ? t('details.infos.non') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('details.infos.tauxRc')}>{sinistre.tauxRc ? `${sinistre.tauxRc}%` : '-'}</Descriptions.Item>
        </Descriptions>
      </div>

      {/* Infos assuré + parties */}
      <div>
        <div className="cb-form-section" style={{ marginBottom: 16 }}>
          {/* Toutes les parties de l'accident au même endroit : l'assuré, puis chaque
            partie adverse avec son immatriculation. Les pastilles disent qui est qui
            d'un coup d'œil ; les organismes restent dans « Parties impliquées ». */}
          <div className="cb-form-section-title"><CarOutlined /> {t('details.infos.assurePartiesImpliquees')}</div>
          <Descriptions column={1} size="small" labelStyle={{ color: '#6b7280', width: 140 }}>
            <Descriptions.Item label={t('details.infos.nomComplet')}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {sinistre.assureNomComplet || '-'}
                <Tag color="green" style={{ borderRadius: 12, fontSize: 10, marginInlineEnd: 0 }}>{t('details.infos.assure')}</Tag>
              </span>
            </Descriptions.Item>
            {/* La police saisie à la déclaration vit sur l'assuré ; un dossier repris ne la
              porte que sur le sinistre. */}
            <Descriptions.Item label={t('details.infos.numeroPolice')}>
              {sinistre.assureNumeroPolice || sinistre.numeroPoliceAssureur || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('details.infos.immatriculation')}>
              <PuceImmatriculation valeur={sinistre.assureImmatriculation} />
            </Descriptions.Item>
          </Descriptions>

          <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 10, paddingTop: 10 }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
              {t('details.infos.partiesAdverses')}{adversaires.length ? ` (${adversaires.length})` : ''}
            </div>
            {adversaires.length === 0 ? (
              <div style={{ fontSize: 13, color: '#9ca3af' }}>{t('details.infos.aucunePartieAdverse')}</div>
            ) : adversaires.map((a) => (
              <div key={a.victimeTrackingId} style={{ padding: '6px 0', borderBottom: '1px dashed #f0f0f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{nomPartieAdverse(a)}</span>
                  <Tag color="volcano" style={{ borderRadius: 12, fontSize: 10, marginInlineEnd: 0 }}>{t('details.infos.partieAdverse')}</Tag>
                  {estAdversaireBlesse(a) && (
                    <Tag color="red" style={{ borderRadius: 12, fontSize: 10, marginInlineEnd: 0 }}>
                      {a.typeVictime === 'DECEDE' ? t('details.infos.decede') : t('details.infos.blesse')}
                    </Tag>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: '#4b5563', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {a.immatriculation ? <PuceImmatriculation valeur={a.immatriculation} />
                    : (a.typeAdversaire || 'ENGINS') === 'ENGINS' && <span style={{ color: '#9ca3af' }}>{t('details.infos.immatriculationNonRenseignee')}</span>}
                  {a.assureurAdverse && <span>{a.assureurAdverse}</span>}
                  {libelleResponsabilite(a.tauxRc) && <span>{libelleResponsabilite(a.tauxRc)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="cb-form-section">
          <div className="cb-form-section-title"><GlobalOutlined /> {t('details.infos.partiesImpliquees')}</div>
          <Descriptions column={1} size="small" labelStyle={{ color: '#6b7280', width: 140 }}>
            <Descriptions.Item label={t('details.infos.paysGestionnaire')}>{sinistre.paysGestionnaireLibelle || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('details.infos.paysEmetteur')}>{sinistre.paysEmetteurLibelle || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('details.infos.assureurPrincipal')}>{sinistre.organismeMembreRaisonSociale || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('details.infos.numeroSinistreAssureur')}>{sinistre.numeroSinistreAssureur || '-'}</Descriptions.Item>
            {/* L'émetteur de l'attestation EST l'assureur principal : il ne s'affiche que
              s'il en diffère, ce qui n'arrive que sur des données reprises. */}
            {sinistre.assureurDeclarant
              && sinistre.assureurDeclarant.trim().toUpperCase() !== (sinistre.organismeMembreRaisonSociale || '').trim().toUpperCase() && (
                <Descriptions.Item label={t('details.infos.assureurEmetteur')}>{sinistre.assureurDeclarant}</Descriptions.Item>
              )}
            <Descriptions.Item label={t('details.infos.homologue')}>{sinistre.organismeHomologueRaisonSociale || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('details.infos.redacteur')}>{sinistre.redacteurNomPrenom || '-'}</Descriptions.Item>
          </Descriptions>
        </div>
      </div>
    </div>
  );
};

/* Corps d'une fiche victime ou partie adverse, pour la saisie et pour l'import Excel. */
/**
 * « La partie adverse est-elle responsable ? », posée au moment d'établir la position
 * RC - et non plus à l'ajout de la partie. La réponse devient la responsabilité de la
 * fiche (`tauxRc`), dont la négociation part ensuite.
 */
const ResponsabiliteAvantRc = ({ adversaire, sinistreId, onEnregistre }) => {
  const { t } = useTranslation('sinistres');
  const [form] = Form.useForm();
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      estResponsable: responsabiliteDepuisTaux(adversaire.tauxRc),
      tauxRc: adversaire.tauxRc != null ? Number(adversaire.tauxRc) : undefined,
    });
  }, [adversaire, form]);

  const enregistrer = async () => {
    const vals = await form.validateFields();
    const tauxRc = tauxRcDepuisSaisie(vals);
    if (tauxRc === null) {
      toast.warning(t('details.responsabilite.indiquezResponsable'));
      return;
    }
    // Rien de changé : le serveur refuserait une version identique.
    if (adversaire.tauxRc != null && Number(adversaire.tauxRc) === tauxRc) {
      toast.info(t('details.responsabilite.inchangee'));
      return;
    }
    setEnvoi(true);
    try {
      const res = await axiosInstance.put(`/v1/victimes/${adversaire.victimeTrackingId}`,
        corpsVictime({ ...adversaire, tauxRc }, sinistreId));
      toast.success(t('details.responsabilite.enregistree'));
      onEnregistre?.(res.data?.data || { ...adversaire, tauxRc });
    } catch (e) {
      toast.error(e?.response?.data?.message || t('details.responsabilite.erreurEnregistrement'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Form form={form} layout="vertical" requiredMark={false}>
      <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: '#6b7280', marginBottom: 8 }}>
        {t('details.responsabilite.titre')}
      </div>
      <ChampResponsabilite form={form} sujet={t('details.responsabilite.sujet')} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" loading={envoi} onClick={enregistrer}
          style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
          {t('details.responsabilite.enregistrer')}
        </Button>
      </div>
    </Form>
  );
};

const corpsVictime = (payload, sinistreId) => ({
  nom: payload.nom,
  email: payload.email || null,
  // Absente : française à la création, inchangée à la modification.
  langue: payload.langue || null,
  prenoms: payload.prenoms,
  dateNaissance: payload.dateNaissance,
  sexe: payload.sexe,
  nationalite: payload.nationalite || null,
  statutActivite: payload.statutActivite,
  revenuMensuel: payload.revenuMensuel ?? 0,
  paysResidenceTrackingId: payload.paysResidenceTrackingId || null,
  sinistreTrackingId: sinistreId,
  typeVictime: payload.typeVictime,
  typeDommage: payload.typeDommage,
  estAdversaire: !!payload.estAdversaire,
  profession: payload.profession || null,
  tauxRc: payload.tauxRc != null ? Number(payload.tauxRc) : null,
  /* Un adversaire est édité via AdversaireModal : on propage aussi sa nature
     et ses détails engin, sinon ces champs ne seraient plus modifiables ici. */
  ...(payload.estAdversaire ? {
    typeAdversaire: payload.typeAdversaire || 'ENGINS',
    typeAdversaireAutre: payload.typeAdversaireAutre || null,
    telephone: payload.telephone || null,
    numeroPermis: payload.numeroPermis || null,
    categoriesPermis: splitCategoriesPermis(payload.categoriesPermis),
    dateDelivrance: payload.dateDelivrance || null,
    lieuDelivrance: payload.lieuDelivrance || null,
    marqueVehicule: payload.marqueVehicule || null,
    modeleVehicule: payload.modeleVehicule || null,
    genreVehicule: payload.genreVehicule || null,
    couleurVehicule: payload.couleurVehicule || null,
    immatriculation: payload.immatriculation || null,
    numeroChassis: payload.numeroChassis || null,
    prochaineVT: payload.prochaineVT || null,
    capaciteVehicule: payload.capaciteVehicule ?? null,
    nbPersonnesABord: payload.nbPersonnesABord || 0,
    proprietaireVehicule: payload.proprietaireVehicule || null,
    aRemorque: !!payload.aRemorque,
    remorqueMarque: payload.remorqueMarque || null,
    remorqueModele: payload.remorqueModele || null,
    remorqueImmatriculation: payload.remorqueImmatriculation || null,
    remorqueAssureur: payload.remorqueAssureur || null,
    assureurAdverse: payload.assureurAdverse || null,
    descriptionDegats: payload.descriptionDegats || null,
    blesses: payload.blesses || 0,
    deces: payload.deces || 0,
  } : {}),
});

/**
 * L'assuré parmi les personnes du dossier.
 *
 * Il n'a pas de fiche victime - il est l'assuré, pas une victime à indemniser - mais sa
 * part de responsabilité commande le calcul de toutes les offres. Demande du 2026-09-16 :
 * le voir après la déclaration, et pouvoir renseigner son taux.
 *
 * Le taux saisi ici est celui du DOSSIER : il s'applique tant qu'aucune part n'est portée
 * par les fiches. Dès qu'une partie adverse ou une victime porte la sienne, la part de
 * l'assuré s'en déduit (100 % moins ces parts) et la saisie n'est plus qu'un repli.
 */
const CarteAssure = ({ sinistre, onEnregistre }) => {
  const { t } = useTranslation('sinistres');
  const [partage, setPartage] = useState(null);
  const [rcOpen, setRcOpen] = useState(false);
  const id = sinistre?.sinistreTrackingId;

  const chargerPartage = useCallback(() => {
    if (!id) return;
    axiosInstance.get(`/v1/sinistres/${id}/partage-responsabilite`, { skipErrorToast: true })
      .then((r) => setPartage(r.data?.data || null))
      .catch(() => setPartage(null));
  }, [id]);
  useEffect(() => { chargerPartage(); }, [chargerPartage, sinistre?.tauxRc]);

  const deduite = partage?.origine === 'PARTIES';
  const part = partage?.partAssure != null ? Number(partage.partAssure) : (sinistre?.tauxRc != null ? Number(sinistre.tauxRc) : null);
  const nom = sinistre?.conducteurEstAssure === false && sinistre?.conducteurNom
    ? `${sinistre.conducteurNom} ${sinistre.conducteurPrenom || ''}`.trim()
    : sinistre?.assureNomComplet;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      padding: '12px 16px', marginBottom: 16, borderRadius: 12,
      background: '#f8fafc', border: '1px solid #e5e7eb',
    }}>
      <Tag color="geekblue" style={{ borderRadius: 20, margin: 0, fontWeight: 600 }}>{t('details.carteAssure.assure')}</Tag>
      <span style={{ fontWeight: 600 }}>{nom || t('details.carteAssure.assureDuDossier')}</span>
      {sinistre?.assureImmatriculation && (
        <span style={{ color: '#6b7280', fontSize: 12.5 }}>{sinistre.assureImmatriculation}</span>
      )}
      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: '#6b7280' }}>
          {t('details.carteAssure.partResponsabilite')}&nbsp;
          <strong style={{ color: '#1a6b3c' }}>{part != null ? `${part} %` : t('details.carteAssure.nonRenseignee')}</strong>
          {deduite && <span>{t('details.carteAssure.deduite')}</span>}
        </span>
        <Button size="small" icon={<SafetyOutlined />} onClick={() => setRcOpen(true)}>
          {t(deduite || !can(HAB.SINISTRE_UPDATE) ? 'details.carteAssure.detail' : (part != null && sinistre?.tauxRc != null ? 'details.carteAssure.modifier' : 'details.carteAssure.etablir'))}
        </Button>
      </span>
      <RcAssureModal
        open={rcOpen}
        onClose={() => setRcOpen(false)}
        sinistre={sinistre}
        peutModifier={can(HAB.SINISTRE_UPDATE)}
        onEnregistre={() => { chargerPartage(); onEnregistre?.(); }}
      />
    </div>
  );
};

/*  Onglet Victimes  */
/**
 * Une entrée du menu des compartiments de l'onglet Victimes.
 *
 * LE COMPTE EST DANS LE MENU, et c'est l'essentiel de son utilité : savoir
 * qu'un dossier porte douze blessés et deux parties adverses AVANT d'ouvrir
 * quoi que ce soit change ce qu'on va y chercher. Empilés, les trois tableaux
 * ne le disaient qu'à celui qui faisait défiler jusqu'en bas.
 *
 * `decale` rentre l'entrée d'un cran : « Corporel » et « Matériel » sont deux
 * façons de trier les mêmes personnes, sous « Victimes ». Les parties adverses
 * en sont d'autres, et restent au niveau supérieur.
 */
const BoutonCompartiment = ({ bloc, actif, onClick, decale = false }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
      border: 'none', cursor: 'pointer', textAlign: 'left',
      borderRadius: 8, padding: '9px 10px', marginBottom: 2,
      paddingLeft: decale ? 22 : 10,
      background: actif ? '#e8f5ed' : 'transparent',
      color: actif ? '#1a6b3c' : '#374151',
      fontWeight: actif ? 600 : 500, fontSize: 13.5,
      transition: 'background 0.15s',
    }}
    onMouseEnter={(e) => { if (!actif) e.currentTarget.style.background = '#f4f6f5'; }}
    onMouseLeave={(e) => { if (!actif) e.currentTarget.style.background = 'transparent'; }}
  >
    <span style={{ color: actif ? '#1a6b3c' : '#9ca3af', display: 'inline-flex', fontSize: 15 }}>
      {bloc.icone}
    </span>
    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {bloc.menu || bloc.titre}
    </span>
    <Tag style={{ borderRadius: 20, marginInlineEnd: 0, fontSize: 11 }}>{bloc.rows.length}</Tag>
  </button>
);

const VictimesTab = ({ sinistreId, sinistre, nombreVictimesAttendu, bilan = null, onBilanUpdated, onDossierModifie }) => {
  const { t } = useTranslation('sinistres');
  const [victimes, setVictimes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Le sablier ne remplace l'onglet qu'au premier chargement. Aux suivants, il
  // démontait aussi la fenêtre ouverte (position RC, partie adverse) : elle
  // disparaissait puis se rouvrait à chaque enregistrement, et revenait à l'étape 1.
  const [dejaCharge, setDejaCharge] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pays, setPays] = useState([]);
  /* Compartiment affiché à droite. Les trois blocs s'empilaient verticalement :
     sur un dossier à quinze victimes, atteindre les parties adverses demandait
     de faire défiler deux tableaux entiers, et l'on ne savait jamais combien il
     en restait. Le menu de gauche les donne tous d'un coup d'œil, avec leur
     compte, et n'en affiche qu'un. */
  const [compartiment, setCompartiment] = useState('CORPOREL');

  // Renvoie la liste rechargee. Le retour est utilise par le rappel « nombre declare »
  // apres un enregistrement : lire l'etat `victimes` juste apres setVictimes donnerait
  // encore la valeur precedente.
  const load = () => {
    setLoading(true);
    return axiosInstance.get(`/v1/victimes/sinistre/${sinistreId}`)
      .then(r => {
        const liste = r.data?.data || [];
        setVictimes(liste);
        return liste;
      })
      .catch(() => [])
      .finally(() => { setLoading(false); setDejaCharge(true); });
  };

  /* Après une modification : relit les fiches ET le dossier. Trancher la dernière
     position RC fait passer le dossier en attente des pièces côté serveur ; sans
     relire le dossier, l'en-tête proposait encore « Établir la RC » jusqu'à F5. */
  const recharger = () => {
    onDossierModifie?.();
    return load();
  };

  /**
   * Rappel apres ajout / modification / retrait d'une fiche victime.
   *
   * Le nombre de victimes annonce a la declaration (`nombreVictimesAttendu`) est saisi
   * AVANT le PV, souvent au telephone. Les fiches, elles, arrivent ensuite. Personne ne
   * pense a revenir corriger le nombre annonce, et l'ecart se propage jusqu'aux etats
   * de reclamation. Le bandeau permanent plus bas signale l'ecart a qui regarde ; ce
   * toast, lui, tombe au seul moment ou l'agent a le dossier en main.
   *
   * Silencieux quand les deux coincident : feliciter d'un etat normal use le canal.
   */
  const rappelerNombreDeclare = (liste) => {
    const attendu = Number(nombreVictimesAttendu);
    if (!attendu) return;
    const reelles = (liste || []).filter(v => !v.estAdversaire).length;
    if (reelles === attendu) return;
    toast.warning(
      t('details.victimes.messages.rappelNombreDeclare', { reelles, attendu }),
      { autoClose: 8000 },
    );
  };

  useEffect(() => {
    load();
    axiosInstance.get('/v1/pays').then(r => setPays(r.data?.data || [])).catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinistreId]);

  /* Le nombre déclaré commande l'ajout de fiches :
       • non renseigné → on ne sait pas combien de victimes le dossier compte ;
       • déjà atteint  → une fiche de plus serait une incohérence.
     Dans les deux cas, la correction se fait dans la carte ci-dessus, avec son
     motif. C'est le seul moyen de garder le nombre annoncé aligné sur la
     réalité : sans blocage, personne ne revient jamais le corriger. */
  const nbFiches = victimes.filter(v => !v.estAdversaire).length;
  const nbAdversaires = victimes.length - nbFiches;
  const attenduCourant = Number(nombreVictimesAttendu) || 0;
  const ajoutBloque = attenduCourant === 0 || nbFiches >= attenduCourant;
  const raisonBlocage = attenduCourant === 0
    ? t('details.victimes.blocage.nombreNonRenseigne')
    : t('details.victimes.blocage.nombreAtteint', { attendu: attenduCourant, fiches: nbFiches });

  const victimesSeules = victimes.filter(v => !v.estAdversaire);
  const adversaires = victimes.filter(v => v.estAdversaire);

  /* Les trois compartiments de l'onglet.
     Une victime MIXTE (legacy - la valeur n'est plus saisissable) porte les deux
     préjudices : elle figure donc à la fois dans le bloc corporel et le matériel. */
  const compartiments = [
    {
      key: 'CORPOREL', titre: t('details.victimes.compartiments.corporelTitre'), menu: t('details.victimes.compartiments.corporelMenu'), prefixe: 'C',
      icone: <MedicineBoxOutlined />,
      vide: t('details.victimes.compartiments.corporelVide'),
      // Une partie adverse blessée ou décédée est aussi une victime : elle figure
      // ici ET dans le bloc des parties adverses.
      rows: [
        ...victimesSeules.filter(v => v.typeDommage === 'CORPOREL' || v.typeDommage === 'MIXTE'),
        ...adversaires.filter(estAdversaireBlesse),
      ],
    },
    {
      key: 'MATERIEL', titre: t('details.victimes.compartiments.materielTitre'), menu: t('details.victimes.compartiments.materielMenu'), prefixe: 'M',
      icone: <CarOutlined />,
      vide: t('details.victimes.compartiments.materielVide'),
      rows: victimesSeules.filter(v => v.typeDommage === 'MATERIEL' || v.typeDommage === 'MIXTE'),
    },
    /* Les adversaires ont leur propre compartiment. Mélangés aux victimes,
       ils gonflaient le compte affiché sans jamais entrer dans le
       rapprochement « déclarées / saisies », qui les exclut : un adversaire
       enregistré se lisait comme une victime absente du décompte. */
    {
      key: 'ADVERSAIRE', titre: t('details.victimes.compartiments.adversairesTitre'), menu: t('details.victimes.compartiments.adversairesMenu'), prefixe: 'A',
      icone: <TeamOutlined />,
      vide: t('details.victimes.compartiments.adversairesVide'),
      rows: adversaires,
    },
  ];

  /* Le compartiment resté sélectionné peut avoir disparu - il n'en disparaît
     aucun aujourd'hui, mais un onglet qui n'affiche rien parce qu'une clé ne
     correspond plus est un défaut muet. On retombe sur le premier. */
  const blocActif = compartiments.find(c => c.key === compartiment) || compartiments[0];

  /* Une partie adverse oubliée à la déclaration s'ajoute ici : sans elle, la
     négociation RC - qui se mène partie par partie - ne peut pas avoir lieu. */
  const [ajoutAdversaire, setAjoutAdversaire] = useState(false);
  const [rcCible, setRcCible] = useState(null);
  const [importEnCours, setImportEnCours] = useState(false);
  const [erreursImport, setErreursImport] = useState([]);

  const openAdd = () => { setEditing(null); setAjoutAdversaire(false); setModalOpen(true); };
  const openAddAdversaire = () => { setEditing(null); setAjoutAdversaire(true); setModalOpen(true); };
  const openEdit = (v) => { setEditing(v); setAjoutAdversaire(false); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditing(null); setAjoutAdversaire(false); };

  const handleSave = async (payload) => {
    if (!editing?.victimeTrackingId) {
      const deja = memePersonneAutreRole(payload, victimes);
      if (deja && !(await confirmerDoublonPersonne(deja, payload))) return;
    }
    setSaving(true);
    try {
      const body = corpsVictime(payload, sinistreId);
      if (editing?.victimeTrackingId) {
        await axiosInstance.put(`/v1/victimes/${editing.victimeTrackingId}`, body);
        toast.success(payload.estAdversaire ? t('details.victimes.messages.partieAdverseModifiee') : t('details.victimes.messages.victimeModifiee'));
      } else {
        await axiosInstance.post('/v1/victimes', body);
        toast.success(payload.estAdversaire ? t('details.victimes.messages.partieAdverseAjoutee') : t('details.victimes.messages.victimeAjoutee'));
      }
      closeModal();
      rappelerNombreDeclare(await recharger());
    } catch (err) {
      toast.error(err?.response?.data?.message || t('details.victimes.messages.erreurEnregistrement'));
    } finally {
      setSaving(false);
    }
  };

  /* Import Excel : même modèle que la déclaration, avec les parties adverses en fin
     de ligne. Les VICTIMES restent soumises au nombre déclaré - un fichier qui en
     apporte plus que le dossier n'en attend est refusé en entier, pour corriger le
     nombre d'abord plutôt que d'importer à moitié. Les parties adverses n'y sont
     pas soumises. Les lignes partent une à une : un refus ne bloque pas les autres. */
  const importerExcel = async (file) => {
    setImportEnCours(true);
    try {
      let lu;
      try {
        lu = await lireVictimesExcel(file);
      } catch {
        toast.error(t('details.victimes.import.fichierIllisible'));
        return;
      }
      const { victimes: lues, adversaires: advs, erreurs } = lu;
      if (lues.length + advs.length === 0) {
        setErreursImport(erreurs);
        toast.error(erreurs.length ? t('details.victimes.import.aucuneLigneExploitable') : t('details.victimes.import.fichierVide'));
        return;
      }
      const place = attenduCourant - nbFiches;
      if (lues.length > 0 && lues.length > place) {
        setErreursImport(erreurs);
        toast.error(attenduCourant === 0
          ? t('details.victimes.import.nombreNonRenseigne') : t('details.victimes.import.tropDeVictimes', { lues: lues.length, place: Math.max(place, 0) }),
          { autoClose: 9000 });
        return;
      }
      const echecs = [...erreurs];
      let faits = 0;
      for (const ligne of [...lues, ...advs]) {
        try {
          await axiosInstance.post('/v1/victimes', corpsVictime(ligne, sinistreId), { skipErrorToast: true });
          faits += 1;
        } catch (e) {
          echecs.push({ ligne: ligne._ligne ?? '?', raison: e?.response?.data?.message || t('details.victimes.import.enregistrementRefuse') });
        }
      }
      setErreursImport(echecs.sort((a, b) => (a.ligne || 0) - (b.ligne || 0)));
      if (faits > 0) {
        toast.success(echecs.length ? t('details.victimes.import.fichesImporteesAvecEchecs', { faits, echecs: echecs.length }) : t('details.victimes.import.fichesImportees', { faits }));
      } else {
        toast.error(t('details.victimes.import.aucuneLigneImportee'));
      }
      rappelerNombreDeclare(await recharger());
    } finally {
      setImportEnCours(false);
    }
  };

  const handleDelete = async (v) => {
    try {
      await axiosInstance.delete(`/v1/victimes/${v.victimeTrackingId}`);
      toast.success(t('details.victimes.messages.victimeRetiree'));
      rappelerNombreDeclare(await recharger());
    } catch (err) {
      toast.error(err?.response?.data?.message || t('details.victimes.messages.erreur'));
    }
  };

  const cols = [
    {
      // La pastille d'ancrage vit sur le nom, pas dans une colonne d'actions : une
      // remarque porte sur LA VICTIME, et le geste doit partir de là où l'œil est.
      // Sans discussion attachée, elle n'apparaît qu'au survol de la ligne (cf.
      // .cb-ancre-discussion) - une icône permanente sur chaque ligne d'un tableau
      // dense ajouterait une colonne de bruit.
      title: t('details.victimes.colonnes.nomPrenom'), key: 'nom', width: 230,
      render: (_, r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>{r.nom} {r.prenoms}</span>
          {estAdversaireBlesse(r) && (
            <Tooltip title={t('details.victimes.colonnes.adversaireAtteintInfobulle')}>
              <Tag color="volcano" style={{ borderRadius: 12, fontSize: 10, marginInlineEnd: 0 }}>
                {r.typeVictime === 'DECEDE' ? t('details.victimes.colonnes.adversaireDecede') : t('details.victimes.colonnes.adversaireBlesse')}
              </Tag>
            </Tooltip>
          )}
          <PastilleDiscussion
            type={ANCRE.VICTIME}
            id={r.victimeTrackingId}
            libelle={`${r.nom || ''} ${r.prenoms || ''}`.trim()}
          />
        </span>
      ),
    },
    { title: t('details.victimes.colonnes.neLe'), dataIndex: 'dateNaissance', render: (v) => formatDate(v), width: 110 },
    { title: t('details.victimes.colonnes.sexe'), dataIndex: 'sexe', width: 60, align: 'center' },
    {
      /* Avancement de l'indemnisation de la personne. NEUTRE (« Ouvert ») : rien n'a
         encore commencé - ni pièces demandées, ni offre. Une partie adverse qui n'est
         pas atteinte n'est pas indemnisée : la colonne ne la concerne pas. */
      title: t('details.victimes.colonnes.statut'), dataIndex: 'statutVictime', width: 130,
      render: (v, r) => {
        if (!estVictimeIndemnisable(r)) return <span style={{ color: '#9ca3af' }}>-</span>;
        const sv = STATUT_VICTIME_MAP[v];
        return (
          <Tooltip title={v === 'NEUTRE' ? t('details.victimes.colonnes.indemnisationPasCommencee') : null}>
            <Tag color={sv?.color} style={{ borderRadius: 20 }}>{sv?.label || v}</Tag>
          </Tooltip>
        );
      },
    },
    {
      /* UNE colonne, une notion : la part de responsabilité de la personne.
         Pour une partie adverse, elle se négocie avec sa compagnie ; tant que ce
         n'est pas tranché, on lit le taux de la fiche et l'état de la négociation,
         une fois tranché, la décision - qui a été recopiée sur la fiche. Deux
         colonnes (« Responsabilité » / « Position RC ») affichaient deux chiffres
         pour la même chose, et ils pouvaient se contredire. */
      title: t('details.victimes.colonnes.responsabilite'), key: 'responsabilite', width: 190,
      render: (_, r) => {
        const tranchee = r.estAdversaire && r.positionRc === 'TRANCHEE';
        const taux = tranchee && r.pourcentageRcFinal != null ? r.pourcentageRcFinal : r.tauxRc;
        const enCours = r.estAdversaire && (r.positionRc === 'EN_NEGOCIATION' || r.positionRc === 'REJETEE');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
            {taux == null
              ? <span style={{ color: '#9ca3af', fontSize: 11 }}>{t('details.victimes.colonnes.nonRenseignee')}</span>
              : Number(taux) > 0
                ? <span style={{ fontWeight: 600, color: '#b45309' }}>{t('details.victimes.colonnes.responsableA', { taux: Number(taux) })}</span>
                : <span style={{ color: '#1a6b3c' }}>{t('details.victimes.colonnes.nonResponsable')}</span>}
            {tranchee && (
              <Tooltip title={t('details.victimes.colonnes.decideeAvecCompagnie')}>
                <Tag color="success" style={{ borderRadius: 12, fontSize: 10, marginInlineEnd: 0 }}>{t('details.victimes.colonnes.tranchee')}</Tag>
              </Tooltip>
            )}
            {enCours && (
              <TagRc
                positionRc={r.positionRc}
                pourcentageRcFinal={r.pourcentageRcFinal != null ? Number(r.pourcentageRcFinal) : null}
                pourcentageRcPropose={r.pourcentageRcPropose != null ? Number(r.pourcentageRcPropose) : null}
                nombreToursRc={r.nombreToursRc}
                dateDerniereActionRc={r.dateDerniereActionRc}
                size="sm"
              />
            )}
          </div>
        );
      },
    },
    { title: t('details.victimes.colonnes.revenuMensuel'), dataIndex: 'revenuMensuel', render: (v) => formatMontant(v), width: 140, align: 'right' },
    { title: t('details.victimes.colonnes.paysResidence'), dataIndex: 'paysResidenceLibelle', ellipsis: true },
    {
      /* Épinglée à droite : les colonnes fixes font ~1 200 px et l'onglet n'en offre
         qu'environ « largeur d'écran − 670 ». Sur un portable, la carte (overflow
         hidden) rognait cette colonne en dernier, et avec elle « Position RC » - le
         bouton semblait réservé à certains profils alors qu'il manquait à l'écran. */
      title: '', key: 'actions', width: 150, align: 'right', fixed: 'right',
      render: (_, r) => (
        <Space size={2}>
          {r.estAdversaire && r.positionRc !== 'TRANCHEE' && (
            <Tooltip title={t('details.victimes.colonnes.definirPositionRc')}>
              <Button
                type="primary" size="small"
                icon={<AuditOutlined />}
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', fontSize: 11 }}
                // Directement sur la négociation : passer par les quatre étapes de la
                // fiche bloquait sur un adversaire incomplet.
                onClick={() => setRcCible(r)}
              >
                {t('details.victimes.colonnes.positionRc')}
              </Button>
            </Tooltip>
          )}
          <Tooltip title={t('details.victimes.colonnes.modifier')}>
            <Button
              type="text" size="small" icon={<EditOutlined />}
              onClick={() => openEdit(r)}
              style={{ color: '#1a6b3c' }}
            />
          </Tooltip>
          <Popconfirm
            title={t('details.victimes.colonnes.retirerVictime')}
            description={t('details.victimes.colonnes.actionIrreversible')}
            onConfirm={() => handleDelete(r)}
            okText={t('details.victimes.colonnes.retirer')} cancelText={t('details.victimes.colonnes.annuler')}
            okButtonProps={{ danger: true }}
          >
            <Tooltip title={t('details.victimes.colonnes.supprimer')}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && !dejaCharge) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
          {/* On compte ce que compte la carte « Nombre de victimes » juste en
              dessous : les fiches de victimes, adversaires exclus. Les deux
              chiffres se contredisaient - l'onglet annonçait « 1 victime » pour
              un adversaire pendant que le rapprochement disait « 0 fiche ». */}
          {t('details.victimes.entete.nbVictimes', { count: nbFiches })}
          {nbAdversaires > 0 && (
            <span style={{ fontWeight: 400, color: '#6b7280' }}>
              {' '}· {t('details.victimes.entete.nbAdversaires', { count: nbAdversaires })}
              {adversaires.some(estAdversaireBlesse) && t('details.victimes.entete.dontAtteints', { count: adversaires.filter(estAdversaireBlesse).length })}
            </span>
          )}
        </h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Tooltip title={t('details.victimes.entete.modeleInfobulle')}>
            <Button icon={<DownloadOutlined />} onClick={telechargerModeleVictimes} style={{ borderRadius: 20, height: 36 }}>
              {t('details.victimes.entete.modele')}
            </Button>
          </Tooltip>
          <Upload accept=".xlsx,.xls" showUploadList={false}
            beforeUpload={(file) => { importerExcel(file); return false; }}>
            <Button icon={<FileExcelOutlined />} loading={importEnCours} style={{ borderRadius: 20, height: 36 }}>
              {t('details.victimes.entete.importer')}
            </Button>
          </Upload>
          <Button icon={<PlusOutlined />} onClick={openAddAdversaire}
            style={{ borderRadius: 20, height: 36, fontWeight: 600, borderColor: '#1a6b3c', color: '#1a6b3c' }}>
            {t('details.victimes.entete.ajouterPartieAdverse')}
          </Button>
          <Tooltip title={ajoutBloque ? raisonBlocage : ''}>
            <span>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openAdd}
                disabled={ajoutBloque}
                style={{
                  borderRadius: 20,
                  height: 36,
                  paddingInline: 20,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  ...(ajoutBloque ? {} : {
                    background: 'linear-gradient(135deg, #1a6b3c 0%, #27ae60 100%)',
                    border: 'none',
                    boxShadow: '0 2px 10px rgba(26, 107, 60, 0.4)',
                  }),
                }}
              >
                {t('details.victimes.entete.ajouterVictime')}
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      {erreursImport.length > 0 && (
        <Alert
          type="warning" showIcon closable onClose={() => setErreursImport([])}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={t('details.victimes.import.lignesNonImportees', { n: erreursImport.length })}
          description={(
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {erreursImport.map((e, i) => <li key={i}>{t('details.victimes.import.ligneErreur', { ligne: e.ligne, raison: e.raison })}</li>)}
            </ul>
          )}
        />
      )}

      {/* L'assuré d'abord : c'est la personne du dossier dont la part commande les offres. */}
      <CarteAssure sinistre={sinistre} onEnregistre={onDossierModifie} />

      {/* Nombre de victimes déclaré - modifiable ici, c'est ce qui débloque l'ajout. */}
      <BilanVictimesCard
        key={`${bilan?.blesses}-${bilan?.decedes}`}
        sinistreId={sinistreId}
        bilan={bilan}
        nbFiches={nbFiches}
        onUpdated={onBilanUpdated}
      />
      {/* Le rapprochement chiffré vit désormais dans la carte ci-dessus ; il ne
          reste ici que la trace du dernier motif, qui n'y a pas sa place. */}
      {bilan?.motif && (
        <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 16, lineHeight: 1.6 }}>
          {t('details.victimes.derniereModificationMotif', { motif: bilan.motif })}
        </div>
      )}
      {/* MAÎTRE-DÉTAIL : les compartiments à gauche, un seul tableau à droite.

          Ils s'empilaient verticalement. Sur un dossier à quinze victimes,
          atteindre les parties adverses demandait de faire défiler deux
          tableaux entiers, et rien ne disait combien il en restait. Le menu
          donne les trois d'un coup d'œil, avec leur compte.

          « Corporel » et « Matériel » sont rangés SOUS « Victimes » : ce sont
          deux façons de trier les mêmes personnes, alors que les parties
          adverses en sont d'autres. Les mettre au même niveau laissait croire
          à trois populations distinctes. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <aside
          className="cb-card"
          style={{ width: 250, flexShrink: 0, padding: 10, position: 'sticky', top: 16 }}
        >
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase',
            color: '#9ca3af', padding: '8px 10px 4px',
          }}>
            {t('details.victimes.menuVictimes')}
          </div>
          {compartiments.filter(c => c.key !== 'ADVERSAIRE').map((c) => (
            <BoutonCompartiment
              key={c.key}
              bloc={c}
              actif={c.key === blocActif.key}
              onClick={() => setCompartiment(c.key)}
              decale
            />
          ))}

          <div style={{ height: 8 }} />
          {compartiments.filter(c => c.key === 'ADVERSAIRE').map((c) => (
            <BoutonCompartiment
              key={c.key}
              bloc={c}
              actif={c.key === blocActif.key}
              onClick={() => setCompartiment(c.key)}
            />
          ))}
        </aside>

        <section style={{ flex: 1, minWidth: 0 }}>
          {(() => {
            const prefixe = blocActif.prefixe;
            const colsBloc = [
              {
                title: t('details.victimes.colonnes.numero'), key: 'num', width: 64, align: 'center',
                render: (_, __, index) => (
                  <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c' }}>
                    {`${prefixe} ${index + 1}/${blocActif.rows.length}`}
                  </span>
                ),
              },
              ...cols,
            ];
            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1a6b3c', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {blocActif.titre}
                  </span>
                  <Tag style={{ borderRadius: 20 }}>{blocActif.rows.length}</Tag>
                </div>
                <Table
                  className="cb-table" dataSource={blocActif.rows} columns={colsBloc}
                  rowKey="victimeTrackingId" size="small" pagination={false} loading={loading}
                  scroll={{ x: 'max-content' }}
                  locale={{ emptyText: <Empty description={blocActif.vide} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                />
              </>
            );
          })()}
        </section>
      </div>
      {editing?.estAdversaire || ajoutAdversaire ? (
        <AdversaireModal
          open={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          onRcUpdated={recharger}
          initial={editing}
          loading={saving}
        />
      ) : (
        <VictimeModal
          open={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          initial={editing}
          pays={pays}
          loading={saving}
        />
      )}

      <CBModal
        open={!!rcCible}
        onCancel={() => setRcCible(null)}
        title={t('details.victimes.modalePositionRc.titre', { nom: [rcCible?.nom, rcCible?.prenoms].filter(Boolean).join(' ') || rcCible?.immatriculation || t('details.victimes.modalePositionRc.partieAdverse') })}
        footer={<Button onClick={() => setRcCible(null)}>{t('details.victimes.modalePositionRc.fermer')}</Button>}
        width={640}
        destroyOnClose
      >
        {rcCible && rcCible.positionRc !== 'TRANCHEE' && (
          <>
            <ResponsabiliteAvantRc
              adversaire={rcCible}
              sinistreId={sinistreId}
              onEnregistre={(maj) => { setRcCible(maj); recharger(); }}
            />
            <Divider style={{ margin: '16px 0' }} />
          </>
        )}
        {rcCible && <NegociationRcPanel adversaire={rcCible} onUpdated={() => recharger()} />}
      </CBModal>
    </div>
  );
};

/*  Onglet PV  */
const PvTab = ({ sinistreId, onRowClick, sinistreStatut }) => {
  const { t } = useTranslation('sinistres');
  const [pvs, setPvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [replaceFromPv, setReplaceFromPv] = useState(null);
  // SOUMISE compte AUSSI comme non valide : c'est une declaration mobile brute, qui
  // precede NOUVEAU. L'oublier laissait le bouton actif, l'agent remplissait le
  // formulaire, et le backend refusait a l'enregistrement (PvSinistreServiceImpl
  // rejette NOUVEAU comme estEnAttenteDeValidation()).
  const dossierNonValide = sinistreStatut === 'NOUVEAU' || sinistreStatut === 'SOUMISE';

  const load = () => {
    setLoading(true);
    axiosInstance.get(`/v1/pv/sinistre/${sinistreId}`)
      .then(r => setPvs(r.data?.data || []))
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinistreId]);

  const cols = [
    {
      title: t('details.pv.colonnes.numeroPv'), dataIndex: 'numeroPv', width: 170,
      render: (v, r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: r.obsolete ? 400 : 600, textDecoration: r.obsolete ? 'line-through' : 'none', color: r.obsolete ? '#9ca3af' : '#111827' }}>
            <NumeroPvAffiche numero={v} />
          </span>
          {r.obsolete
            ? <Tag color="red" style={{ borderRadius: 20, fontSize: 10 }}>{t('details.pv.colonnes.obsolete')}</Tag>
            : <Tag color="green" style={{ borderRadius: 20, fontSize: 10 }}>{t('details.pv.colonnes.actif')}</Tag>}
        </span>
      ),
    },
    { title: t('details.pv.colonnes.sens'), dataIndex: 'sensCirculation', width: 70, render: (v) => <Tag>{v}</Tag> },
    { title: t('details.pv.colonnes.entiteConstat'), dataIndex: 'entiteConstatNom', ellipsis: true },
    { title: t('details.pv.colonnes.dateAccidentPv'), dataIndex: 'dateAccidentPv', render: (v) => formatDate(v), width: 130 },
    { title: t('details.pv.colonnes.dateReception'), dataIndex: 'dateReceptionBncb', render: (v) => formatDate(v), width: 130 },
    {
      title: t('details.pv.colonnes.complet'), dataIndex: 'estComplet', width: 90, align: 'center',
      render: (v) => v ? <Tag color="green" style={{ borderRadius: 20 }}>{t('details.pv.colonnes.oui')}</Tag> : <Tag color="red" style={{ borderRadius: 20 }}>{t('details.pv.colonnes.non')}</Tag>,
    },
    {
      title: '', key: 'actions', width: 130, align: 'right',
      render: (_, r) => !r.obsolete && (
        <Tooltip title={t('details.pv.colonnes.remplacerInfobulle')}>
          <Button
            size="small"
            type="default"
            icon={<EditOutlined />}
            onClick={(e) => { e.stopPropagation(); setReplaceFromPv(r); }}
          >
            {t('details.pv.colonnes.remplacer')}
          </Button>
        </Tooltip>
      ),
    },
  ];

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  const obsoleteCount = pvs.filter(p => p.obsolete).length;
  /* Un dossier ne porte qu'UN PV actif : le corriger se fait par « Remplacer »,
     qui verse l'ancien à l'historique et le marque obsolète. Tant qu'un PV actif
     est là, « Enregistrer un PV » n'a donc plus de sens - il empilait des PV
     concurrents sans que rien ne tranche lequel fait foi. */
  const pvActif = pvs.find(p => !p.obsolete) || null;
  const ajoutPvBloque = dossierNonValide || Boolean(pvActif);
  const raisonAjoutPv = dossierNonValide
    ? t('details.pv.doitEtreValide')
    : (pvActif
      ? t('details.pv.pvActif', { numero: pvActif.numeroPv || '' })
      : '');
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          {t('details.pv.nbPv', { count: pvs.length })}
          {obsoleteCount > 0 && (
            <span style={{ marginLeft: 8 }}>
              · <Tag color="red" style={{ borderRadius: 20, fontSize: 10 }}>{t('details.pv.nbObsoletes', { count: obsoleteCount })}</Tag>
            </span>
          )}
        </div>
        <Tooltip title={raisonAjoutPv}>
          <span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={ajoutPvBloque}
              onClick={() => setCreateOpen(true)}
              style={ajoutPvBloque ? {} : { background: '#1a6b3c', borderColor: '#1a6b3c' }}
            >
              {t('details.pv.enregistrerPv')}
            </Button>
          </span>
        </Tooltip>
      </div>
      {dossierNonValide && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12, borderRadius: 10 }}
          message={t('details.pv.pvBloque')}
        />
      )}
      <Table className="cb-table" dataSource={pvs} columns={cols} rowKey="pvTrackingId"
        size="small" pagination={false}
        rowClassName={(r) => r.obsolete ? 'cb-row-obsolete' : ''}
        onRow={(r) => ({
          onClick: () => onRowClick && onRowClick(r.pvTrackingId),
          style: { cursor: onRowClick ? 'pointer' : 'default', opacity: r.obsolete ? 0.6 : 1 },
        })}
        locale={{ emptyText: <Empty description={t('details.pv.aucunPv')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      />
      <NouveauPvModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
        defaultSinistreId={sinistreId}
      />
      <NouveauPvModal
        open={Boolean(replaceFromPv)}
        onClose={() => setReplaceFromPv(null)}
        onCreated={() => { setReplaceFromPv(null); load(); }}
        defaultSinistreId={sinistreId}
        replaceFromPv={replaceFromPv}
      />
    </div>
  );
};

/*  Onglet Documents (OssanGED)  */
const DocumentsTab = ({ sinistreId, navigate }) => {
  const { t } = useTranslation('sinistres');
  const [docs, setDocs] = useState([]);
  const [victimes, setVictimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        const [docsRes, victimesRes] = await Promise.all([
          axiosInstance.get(`/v1/ged/sinistres/${sinistreId}/documents`).catch(() => ({ data: { data: [] } })),
          axiosInstance.get(`/v1/victimes/sinistre/${sinistreId}`).catch(() => ({ data: { data: [] } })),
        ]);

        const docsData = docsRes.data?.data || [];
        // Une partie adverse blessée a ses pièces de réclamation comme une victime.
        const victimesData = (victimesRes.data?.data || []).filter(estVictimeIndemnisable);
        const victimesEnrichies = await Promise.all(victimesData.map(async (victime) => {
          const [docsVictimeRes, dossiersRes] = await Promise.all([
            axiosInstance.get(`/v1/ged/victimes/${victime.victimeTrackingId}/documents`).catch(() => ({ data: { data: [] } })),
            axiosInstance.get(`/v1/dossiers-reclamation/victime/${victime.victimeTrackingId}`).catch(() => ({ data: { data: [] } })),
          ]);
          const docsVictime = docsVictimeRes.data?.data || [];
          const dossiers = Array.isArray(dossiersRes.data?.data) ? dossiersRes.data.data : [];
          return {
            ...victime,
            docsGed: docsVictime,
            dossiersReclamation: dossiers,
            dossierActif: getCurrentDossier(dossiers),
          };
        }));

        if (cancelled) return;
        setDocs(docsData);
        setVictimes(victimesEnrichies);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [sinistreId, refreshKey]);

  const telecharger = async (doc) => {
    try {
      const res = await axiosInstance.get(`/v1/ged/documents/${doc.ossanGedDocumentId}/telecharger`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: doc.mimeType || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.titre || 'document'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error(t('details.documents.telechargementImpossible')); }
  };

  const cols = [
    { title: t('details.documents.colonnes.titre'), dataIndex: 'titre', ellipsis: true },
    { title: t('details.documents.colonnes.type'), dataIndex: 'typeDocument', width: 180, render: v => <Tag color="blue" style={{ borderRadius: 20 }}>{v?.replace(/_/g, ' ')}</Tag> },
    { title: t('details.documents.colonnes.dateDoc'), dataIndex: 'dateDocument', width: 110, render: v => formatDate(v) },
    { title: t('details.documents.colonnes.victime'), dataIndex: 'victimeNomPrenom', width: 180, ellipsis: true, render: v => v || '-' },
    {
      title: '', width: 160, align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setPreviewDoc(r)}
            disabled={!r.ossanGedDocumentId}
          >
            {t('details.documents.apercu')}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => telecharger(r)}
          >
            PDF
          </Button>
        </Space>
      ),
    },
  ];

  const docsSansVictime = docs.filter(doc => !doc.victimeTrackingId && !doc.victimeNomPrenom);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <GedUploader sinistreId={sinistreId} onUploaded={() => setRefreshKey(k => k + 1)} />
      </div>

      <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: 20, marginTop: 20 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>
          <FolderOpenOutlined /> {t('details.documents.documentsDuSinistre', { n: docs.length })}
        </h3>
        {loading ? <Spin /> : (
          <Table className="cb-table" dataSource={docs} columns={cols}
            rowKey="ossanGedDocumentTrackingId" size="small" pagination={{ pageSize: 10 }}
            locale={{ emptyText: <Empty description={t('details.documents.aucunDocumentIndexe')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        )}
      </div>

      <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: 20, marginTop: 20 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>
          <UserOutlined /> {t('details.documents.suiviParVictime')}
        </h3>

        {loading ? <Spin /> : victimes.length === 0 ? (
          <Empty description={t('details.documents.aucuneVictime')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {victimes.map(victime => {
              const dossier = victime.dossierActif;
              const statutDossier = dossier?.statut ? (STATUT_DOSSIER_MAP[dossier.statut] || { label: dossier.statut, color: 'default' }) : null;
              const nbPiecesRecues = dossier?.nbPiecesRecues ?? 0;
              const nbPiecesRequises = dossier?.nbPiecesRequises ?? 0;
              const pct = nbPiecesRequises > 0 ? Math.round((nbPiecesRecues / nbPiecesRequises) * 100) : 0;

              return (
                <div key={victime.victimeTrackingId} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 12 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <strong style={{ fontSize: 15 }}>{victime.nom} {victime.prenoms}</strong>
                        <Tag color={victime.typeVictime === 'DECEDE' ? 'red' : victime.typeVictime === 'BLESSE' ? 'orange' : 'default'} style={{ borderRadius: 20 }}>
                          {victime.typeVictime || 'VICTIME'}
                        </Tag>
                        {dossier?.estMur != null && (
                          <Tag
                            color={dossier.estMur
                              ? 'success'
                              : (dossier.piecesCompletes && dossier.consolidationRequise && !dossier.estConsolidee)
                                ? 'purple' : 'gold'}
                            style={{ borderRadius: 20 }}
                          >
                            {dossier.estMur
                              ? t('details.documents.dossierMur')
                              : (dossier.piecesCompletes && dossier.consolidationRequise && !dossier.estConsolidee)
                                ? t('details.documents.attenteConsolidation') : t('details.documents.collecteEnCours')}
                          </Tag>
                        )}
                        {statutDossier && (
                          <Tag color={statutDossier.color} style={{ borderRadius: 20 }}>
                            {statutDossier.label}
                          </Tag>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {t('details.documents.nbDocumentsGed', { count: victime.docsGed.length })}
                        {dossier?.numeroDossier ? t('details.documents.dossierNumero', { numero: dossier.numeroDossier }) : t('details.documents.aucunDossierReclamation')}
                      </div>
                    </div>

                    {dossier?.dossierTrackingId && (
                      <Button size="small" onClick={() => navigate(`/reclamations/${dossier.dossierTrackingId}`)}>
                        {t('details.documents.ouvrirDossier')}
                      </Button>
                    )}
                  </div>

                  {dossier ? (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: '#6b7280' }}>
                        <span>{t('details.documents.piecesRecues')}</span>
                        <span>{t('details.documents.obligatoires', { recues: nbPiecesRecues, requises: nbPiecesRequises || '-' })}</span>
                      </div>
                      <Progress
                        percent={pct}
                        status={dossier.estMur ? 'success' : 'active'}
                        strokeColor={dossier.estMur ? '#166534' : '#1a6b3c'}
                      />
                    </div>
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ borderRadius: 10, marginBottom: 14 }}
                      message={t('details.documents.aucunDossierOuvert')}
                    />
                  )}

                  <Table
                    className="cb-table"
                    dataSource={victime.docsGed}
                    columns={cols}
                    rowKey={(r) => r.ossanGedDocumentTrackingId || r.ossanGedDocumentId || `${victime.victimeTrackingId}-${r.titre}-${r.dateDocument || 'na'}`}
                    size="small"
                    pagination={false}
                    locale={{ emptyText: <Empty description={t('details.documents.aucunDocumentVictime')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                  />
                </div>
              );
            })}

            {docsSansVictime.length > 0 && (
              <div style={{ border: '1px dashed #e5e7eb', borderRadius: 12, padding: 16, background: '#fafafa' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t('details.documents.documentsNonRattaches')}</div>
                <Table
                  className="cb-table"
                  dataSource={docsSansVictime}
                  columns={cols}
                  rowKey={(r) => r.ossanGedDocumentTrackingId || r.ossanGedDocumentId || `${r.titre}-${r.dateDocument || 'na'}`}
                  size="small"
                  pagination={false}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <DocumentPreviewModal
        doc={previewDoc}
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </div>
  );
};

/*  Onglet Courriers  */
const CourriersTab = ({ sinistreId }) => {
  const { t } = useTranslation('sinistres');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance.get(`/v1/courriers/sinistre/${sinistreId}`)
      .then(r => setItems(r.data?.data || []))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [sinistreId]);

  const cols = [
    { title: t('details.courriers.colonnes.reference'), dataIndex: 'referenceCourrier', width: 160 },
    { title: t('details.courriers.colonnes.type'), dataIndex: 'typeCourrier', width: 100, render: (v) => <Tag color={v === 'ENTRANT' ? 'blue' : 'green'} style={{ borderRadius: 20 }}>{v}</Tag> },
    { title: t('details.courriers.colonnes.objet'), dataIndex: 'objet', ellipsis: true },
    { title: t('details.courriers.colonnes.expediteur'), dataIndex: 'expediteur', width: 160, ellipsis: true },
    { title: t('details.courriers.colonnes.date'), dataIndex: 'dateCourrier', render: (v) => formatDate(v), width: 110 },
    { title: t('details.courriers.colonnes.traite'), dataIndex: 'traite', width: 80, align: 'center', render: (v) => v ? <Tag color="green">{t('details.courriers.oui')}</Tag> : <Tag>{t('details.courriers.non')}</Tag> },
  ];

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  return (
    <Table className="cb-table" dataSource={items} columns={cols} rowKey="courrierTrackingId"
      size="small" pagination={false}
      locale={{ emptyText: <Empty description={t('details.courriers.aucunCourrier')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
    />
  );
};

/*  Journal helpers  */
const J_BRAND = '#1a6b3c';
const J_DANGER = '#ef4444';
const J_WARN = '#d97706';

const fmtDateTime = (d) => d ? dayjs(d).format(i18n.t('sinistres:details.journal.formatDateHeure')) : '-';

/**
 * Ce qui bloque la maturite du dossier, en une phrase.
 *
 * Le serveur rend un motif par victime. Les empiler serait illisible sur un
 * dossier a cinq victimes : on affiche celui qui concerne la premiere victime
 * non mure, en indiquant combien d'autres partagent le meme sort.
 */
function motifBlocageMaturite(maturite) {
  const bloquees = (maturite?.victimes || []).filter((v) => !v.estMur && v.motifBlocage);
  if (bloquees.length === 0) return null;
  const [premiere, ...autres] = bloquees;
  const nom = premiere.victimeNomComplet || premiere.numeroDossier;
  const suite = autres.length > 0
    ? i18n.t('sinistres:details.maturite.autresVictimesEnAttente', { count: autres.length })
    : '';
  return { texte: i18n.t('sinistres:details.maturite.motifBlocage', { nom, motif: premiere.motifBlocage, suite }), etat: etatMaturitePersonne(premiere) };
}

/**
 * Où en est une personne à indemniser, en un mot - ce que la barre et les pastilles
 * affichent. Une victime sans dossier de réclamation n'a encore rien fourni : elle
 * compte, et se voit.
 */
function etatMaturitePersonne(v) {
  if (v.estMur) return 'MUR';
  if (!v.dossierTrackingId) return 'SANS_DOSSIER';
  if (v.piecesCompletes && v.consolidationRequise && !v.estConsolidee) return 'CONSOLIDATION';
  return 'PIECES';
}

const ETATS_MATURITE = {
  MUR: { libelle: 'details.maturite.etats.mure', couleur: '#166534', tag: 'success' },
  CONSOLIDATION: { libelle: 'details.maturite.etats.attenteConsolidation', couleur: '#7c3aed', tag: 'purple' },
  PIECES: { libelle: 'details.maturite.etats.piecesEnCours', couleur: '#d97706', tag: 'gold' },
  SANS_DOSSIER: { libelle: 'details.maturite.etats.dossierNonOuvert', couleur: '#9ca3af', tag: 'default' },
};

/**
 * Frise du dossier, a partir de deux sources et deux seulement.
 *
 * - `sinistre` porte les JALONS metier, dates posees sur le dossier lui-meme
 *   (bon a payer, cheque recu, solde...). Elles ne vivent nulle part ailleurs.
 * - `tracabilite` porte les ACTES, fusionnes cote serveur : versions,
 *   affectations, courriers, pieces GED, validations.
 *
 * Le navigateur assemblait auparavant quatre appels distincts et n'avait acces
 * ni aux validations, ni a l'avant → apres.
 */
function buildTimeline(sinistre, tracabilite = [], t) {
  if (!sinistre) return [];
  const evts = [];
  const push = (titre, date, opts = {}) => { if (date) evts.push({ titre, date, ...opts }); };

  // dateDeclaration (date metier, stable a travers les versions), PAS createdAt : depuis le
  // versioning a tracking id stable, createdAt de la version active vaut la date de la DERNIERE
  // modification. L'utiliser afficherait la declaration a cette date, en doublon avec l'entree
  // « Dossier modifie ». On perd l'heure du jour, mais on affiche la bonne date.
  push(t('details.journal.evenements.sinistreDeclare'), sinistre.dateDeclaration || sinistre.createdAt, {
    auteur: sinistre.redacteurNomPrenom,
    description: [sinistre.ville, sinistre.lieuAccident].filter(Boolean).join(' - ') || null,
    accent: J_BRAND, icon: <ProfileOutlined />,
  });
  push(t('details.journal.evenements.dossierValide'), sinistre.dateValidation, {
    accent: J_BRAND, icon: <CheckCircleOutlined />, description: t('details.journal.evenements.controleEffectue'),
  });
  if (sinistre.dateConfirmationGarantie) {
    push(
      sinistre.garantieAcquise ? t('details.journal.evenements.garantieConfirmee') : t('details.journal.evenements.garantieNonAcquise'),
      sinistre.dateConfirmationGarantie,
      {
        accent: sinistre.garantieAcquise ? J_BRAND : J_DANGER,
        icon: sinistre.garantieAcquise ? <SafetyOutlined /> : <StopOutlined />,
        description: sinistre.courrierNonGarantieRef || null,
      }
    );
  }
  if (sinistre.datePvRecu) {
    push(t('details.journal.evenements.pvRecu'), sinistre.datePvRecu, {
      accent: sinistre.pvComplet ? J_BRAND : J_WARN, icon: <FileTextOutlined />,
      description: sinistre.pvComplet ? t('details.journal.evenements.pvComplet') : t('details.journal.evenements.pvIncomplet'),
    });
  }
  if (sinistre.positionRc && sinistre.positionRc !== 'EN_ATTENTE') {
    push(
      sinistre.positionRc === 'ACCEPTE' ? t('details.journal.evenements.positionRcAcceptee') : t('details.journal.evenements.positionRcRejetee'),
      sinistre.datePositionRc || sinistre.dateConfirmationGarantie,
      { accent: sinistre.positionRc === 'ACCEPTE' ? J_BRAND : J_DANGER, icon: sinistre.positionRc === 'ACCEPTE' ? <AuditOutlined /> : <CloseCircleOutlined /> }
    );
  }
  push(t('details.journal.evenements.offreCalculee'), sinistre.dateOffre, { accent: J_BRAND, icon: <DollarOutlined /> });
  push(t('details.journal.evenements.accordVictime'), sinistre.dateAccord, { accent: J_BRAND, icon: <SmileOutlined /> });
  push(t('details.journal.evenements.rejetOffre'), sinistre.dateRejet, { accent: J_DANGER, icon: <RollbackOutlined /> });
  push(t('details.journal.evenements.bapEmis'), sinistre.dateBap, { accent: J_BRAND, icon: <FileDoneOutlined /> });
  push(t('details.journal.evenements.chequeRecu'), sinistre.dateChequeRecu, { accent: J_BRAND, icon: <BankOutlined /> });
  push(t('details.journal.evenements.dossierSolde'), sinistre.dateSolde, { accent: J_BRAND, icon: <CheckOutlined /> });
  push(t('details.journal.evenements.dossierCloture'), sinistre.dateCloture, { accent: '#6b7280', icon: <FolderOutlined /> });
  if (sinistre.estContentieux) {
    push(t('details.journal.evenements.passageContentieux'), sinistre.dateContentieux || sinistre.dateCloture, { accent: J_DANGER, icon: <WarningOutlined /> });
  }
  // Actes fusionnés côté serveur. La puce et l'icône se déduisent de la catégorie,
  // pas d'un `if` par cas : une catégorie ajoutée demain s'affiche sans retouche.
  tracabilite.forEach(e => push(e.action, e.date, {
    auteur: e.auteur,
    description: e.detail || null,
    avant: e.valeurAvant,
    apres: e.valeurApres,
    ...(APPARENCE_TRACE[e.categorie] || APPARENCE_TRACE.DEFAUT),
  }));
  return evts.filter(e => e.date).sort((a, b) => new Date(b.date) - new Date(a.date));
}

/** Puce et icône par catégorie d'acte, rendues par `JournalTab`. */
const APPARENCE_TRACE = {
  CREATION: { accent: J_BRAND, icon: <ProfileOutlined /> },
  STATUT: { accent: J_BRAND, icon: <SwapOutlined /> },
  MODIFICATION: { accent: '#9ca3af', icon: <EditOutlined /> },
  AFFECTATION: { accent: J_BRAND, icon: <UserSwitchOutlined /> },
  PRISE_EN_CHARGE: { accent: J_BRAND, icon: <CheckOutlined /> },
  RELANCE: { accent: J_WARN, icon: <MailOutlined /> },
  COURRIER: { accent: '#6b7280', icon: <MailOutlined /> },
  // Courriel automatique de demande de garantie (bureau homologue pour un ET,
  // compagnie pour un TE) ; le détail porte l'adresse réellement utilisée.
  DEMANDE_GARANTIE: { accent: J_BRAND, icon: <MailOutlined /> },
  COURRIEL_NON_ENVOYE: { accent: J_WARN, icon: <MailOutlined /> },
  DOCUMENT: { accent: '#6b7280', icon: <PaperClipOutlined /> },
  VALIDATION: { accent: J_BRAND, icon: <SafetyOutlined /> },
  ATTENTE_OUVERTE: { accent: J_WARN, icon: <ClockCircleOutlined /> },
  ATTENTE_CLOTUREE: { accent: J_BRAND, icon: <CheckCircleOutlined /> },
  // Indice, jamais preuve : le libelle rendu par le serveur le dit lui-meme.
  OUVERTURE_COURRIEL: { accent: '#9ca3af', icon: <EyeOutlined /> },
  DEFAUT: { accent: '#d1d5db', icon: <EditOutlined /> },
};

/**
 * Historique du dossier : son cycle de vie, du plus recent au plus ancien.
 *
 * Le bandeau de tete rappelle OU en est le dossier maintenant. Sans lui, on lit
 * une suite d'evenements sans savoir lequel decrit l'etat courant - l'information
 * la plus utile etait celle qu'il fallait deduire.
 */
function JournalTab({ evenements, statutCourant, redacteur }) {
  const { t } = useTranslation('sinistres');
  if (evenements.length === 0) {
    return <Empty description={t('details.journal.aucunEvenement')} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }
  const meta = STATUT_MAP[statutCourant];
  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 16px', marginBottom: 20,
        background: '#f8fafc', border: '1px solid #eceff3', borderRadius: 10,
      }}>
        <span style={{ fontSize: 12.5, color: '#667085' }}>{t('details.journal.etapeActuelle')}</span>
        {meta
          ? <Tag color={meta.color} style={{ borderRadius: 14, fontWeight: 650, marginInlineEnd: 0 }}>{meta.label}</Tag>
          : <span style={{ fontWeight: 650 }}>{statutCourant || '-'}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#667085' }}>
          {redacteur ? t('details.journal.suiviPar', { redacteur }) : t('details.journal.nonAffecte')}
          {' · '}
          {t('details.journal.nbEvenements', { count: evenements.length })}
        </span>
      </div>

      <ol style={{ listStyle: 'none', margin: 0, padding: 0, position: 'relative' }}>
        <div style={{ position: 'absolute', left: 7, top: 6, bottom: 6, width: 1, background: '#e5e7eb' }} />
        {evenements.map((e, i) => (
          <li key={i} style={{ position: 'relative', paddingLeft: 28, paddingBottom: i < evenements.length - 1 ? 22 : 0 }}>
            <span style={{
              position: 'absolute', left: 1, top: 5,
              width: 13, height: 13, borderRadius: '50%',
              background: '#fff', border: `2px solid ${e.accent || '#d1d5db'}`,
            }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
              {e.icon && <span style={{ marginRight: 6 }}>{e.icon}</span>}
              {e.titre}
            </div>
            <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>
              <ClockCircleOutlined style={{ fontSize: 10, marginRight: 4 }} />
              {fmtDateTime(e.date)}{e.auteur && <span> · {e.auteur}</span>}
            </div>
            {(e.avant || e.apres) && (
              <div style={{ fontSize: 12, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {e.avant && (
                  <span style={{
                    padding: '1px 7px', borderRadius: 4, background: '#f3f4f6',
                    color: '#6b7280', textDecoration: 'line-through',
                  }}>{e.avant}</span>
                )}
                {e.avant && e.apres && <span style={{ color: '#9ca3af' }}>→</span>}
                {e.apres && (
                  <span style={{
                    padding: '1px 7px', borderRadius: 4, background: '#e8f5ed',
                    color: '#1a6b3c', fontWeight: 600,
                  }}>{e.apres}</span>
                )}
              </div>
            )}
            {e.description && (
              <div style={{ fontSize: 12.5, color: '#374151', marginTop: 4, lineHeight: 1.5 }}>
                {e.description}
              </div>
            )}
          </li>
        ))}
      </ol>
    </>
  );
}

/*  Page principale  */
export default function DetailsSinistre() {
  const { t } = useTranslation('sinistres');
  const { id } = useParams();
  const navigate = useNavigate();
  const [sinistre, setSinistre] = useState(null);
  const [assure, setAssure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'infos');
  // Detail victime par victime du bloc maturite : replie par defaut. Voir le rendu
  // du bloc pour la raison.
  const [maturiteDetail, setMaturiteDetail] = useState(false);
  const [victimes, setVictimes] = useState([]);
  const [pvs, setPvs] = useState([]);
  // PV en vigueur : c'est lui qui fait foi sur le lieu et la date de l'accident.
  const pvActifDossier = pvs.find((p) => !p.obsolete) || pvs[0] || null;
  /* Offre acceptée → règlement : le formulaire du règlement s'ouvre sur l'onglet
     Règlements, déjà rempli du montant accordé et de la victime. */
  const [prefillReglement, setPrefillReglement] = useState(null);
  const [pvCreateOpen, setPvCreateOpen] = useState(false);
  const [pvDetailId, setPvDetailId] = useState(null);
  const [contentieuxOpen, setContentieuxOpen] = useState(false);
  const [arbitrageOpen, setArbitrageOpen] = useState(false);
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [garantieModalOpen, setGarantieModalOpen] = useState(false);
  const [demandePvOpen, setDemandePvOpen] = useState(false);
  /* La demande de PV est-elle DÉJÀ partie ? Sans cette réponse, l'action principale
     reproposait « Demander le PV » à un dossier dont la demande était envoyée, et
     le clic rouvrait le même courriel au lieu de faire avancer quoi que ce soit. */
  const [demandePvEnvoyee, setDemandePvEnvoyee] = useState(false);
  const [rejeterModalOpen, setRejeterModalOpen] = useState(false);
  const [statutModalOpen, setStatutModalOpen] = useState(false);
  const [savingLitige, setSavingLitige] = useState(false);
  const [savingValidation, setSavingValidation] = useState(false);
  const [savingGarantie, setSavingGarantie] = useState(false);
  const [savingRejet, setSavingRejet] = useState(false);
  // TE : la fiche de déclaration du bureau homologue est-elle dans la GED ?
  // null tant qu'on ne sait pas - ni bandeau ni action ne s'affichent sur une supposition.
  const [ficheHomologue, setFicheHomologue] = useState(null);
  const [ficheModalOpen, setFicheModalOpen] = useState(false);
  const [savingFiche, setSavingFiche] = useState(false);

  const loadSinistre = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/v1/sinistres/${id}`);
      setSinistre(res.data?.data);
      // L'historique des versions n'est plus appelé ici : il fait partie de la
      // traçabilité fusionnée côté serveur (/tracabilite).
    } catch {
      toast.error(i18n.t('sinistres:details.messages.sinistreIntrouvable'));
      setSinistre(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  /* Relit le dossier sans le sablier de page : `loadSinistre` remplace toute la fiche
     par un sablier, ce qui démonte les onglets et la fenêtre ouverte dedans. */
  const rafraichirSinistre = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/sinistres/${id}`);
      if (res.data?.data) setSinistre(res.data.data);
    } catch { /* la fiche garde l'état affiché, le prochain chargement le corrigera */ }
  }, [id]);

  const loadVictimes = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/victimes/sinistre/${id}`);
      setVictimes(res.data?.data || []);
    } catch {
      setVictimes([]);
    }
  }, [id]);

  const loadPvs = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/pv/sinistre/${id}`);
      setPvs(res.data?.data || []);
    } catch { setPvs([]); }
  }, [id]);

  /* ─ Traçabilité du dossier ────────────────────────────────────────────────
     Un seul appel là où il en fallait quatre (versions, affectations, documents
     GED, courriers), assemblés à la main dans le navigateur. Le serveur fusionne
     désormais ces sources - plus les validations, qui ne laissaient de trace
     nulle part - et rend une frise déjà ordonnée, avec l'avant → après quand il
     a du sens. */
  const [tracabilite, setTracabilite] = useState([]);
  // Rechargé après une relance envoyée depuis l'onglet Délais, pour que la frise
  // reflète l'envoi sans attendre un rechargement de la page.
  const [journalTick, setJournalTick] = useState(0);
  useEffect(() => {
    let monte = true;
    axiosInstance.get(`/v1/sinistres/${id}/tracabilite`, { skipErrorToast: true })
      .then((r) => { if (monte) setTracabilite(r.data?.data ?? []); })
      .catch(() => { /* silencieux : la frise se limite alors aux jalons du dossier */ });
    return () => { monte = false; };
  }, [id, journalTick]);

  // ─ Maturité pondérée par victime : x/N victimes mûres (pièces + consolidation corporel) ─
  const [maturite, setMaturite] = useState(null);
  const [consolidationOpen, setConsolidationOpen] = useState(false);
  const [rcAssureOpen, setRcAssureOpen] = useState(false);
  const loadMaturite = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/sinistres/${id}/maturite`);
      setMaturite(res.data?.data || null);
    } catch { setMaturite(null); }
  }, [id]);
  useEffect(() => { loadMaturite(); }, [loadMaturite]);

  // ─ Chargement des délais & pénalités pour badge + bannière d'alerte ─
  const [delaisDuSinistre, setDelaisDuSinistre] = useState([]);
  const loadDelais = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/delais/sinistre/${id}`);
      setDelaisDuSinistre(res.data?.data || []);
    } catch { setDelaisDuSinistre([]); }
  }, [id]);
  useEffect(() => { loadDelais(); }, [loadDelais]);

  const delaisActifs = delaisDuSinistre.filter(d => d.statut !== 'RESOLU');
  const nbCritiques = delaisActifs.filter(d => d.niveauAlerte === 'CRITIQUE').length;
  const nbUrgents = delaisActifs.filter(d => d.niveauAlerte === 'URGENT').length;
  const nbAttention = delaisActifs.filter(d => d.niveauAlerte === 'ATTENTION').length;
  const nbAlertesTotal = nbCritiques + nbUrgents + nbAttention;

  // Hydrate les infos de l'assuré (utilisées par ValidationModal)
  useEffect(() => {
    if (!sinistre?.assureTrackingId) { setAssure(null); return; }
    axiosInstance.get(`/v1/assures/${sinistre.assureTrackingId}`)
      .then(r => setAssure(r.data?.data || null))
      .catch(() => setAssure(null));
  }, [sinistre?.assureTrackingId]);

  useEffect(() => {
    loadSinistre();
  }, [loadSinistre]);

  const estTe = estSinistreTe(sinistre);
  const loadFicheHomologue = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/ged/sinistres/${id}/documents`, { skipErrorToast: true });
      setFicheHomologue(ficheHomologueParmi(res.data?.data));
    } catch { setFicheHomologue(null); }
  }, [id]);
  useEffect(() => {
    if (estTe) loadFicheHomologue(); else setFicheHomologue(null);
  }, [estTe, loadFicheHomologue]);

  useEffect(() => {
    if (!estTe || sinistre?.statut !== 'ATTENTE_PV' || !sinistre?.sinistreTrackingId) {
      setDemandePvEnvoyee(false);
      return;
    }
    /* On interroge l'aperçu À CHAQUE FOIS, et le garde-fou de session ne retient
       plus que la PROPOSITION spontanée du courriel. Auparavant il coupait aussi
       l'appel : au rechargement d'un dossier dont la demande était déjà partie,
       l'écran l'ignorait et reproposait de la faire. */
    axiosInstance.get(`/v1/sinistres/${sinistre.sinistreTrackingId}/demande-pv/apercu`, { skipErrorToast: true })
      .then((r) => {
        const envoye = r.data?.data?.statutEnvoi === 'ENVOYE';
        setDemandePvEnvoyee(envoye);
        if (envoye) return;
        const cle = `cb.demande-pv.proposee.${sinistre.sinistreTrackingId}`;
        try {
          if (sessionStorage.getItem(cle)) return;
          sessionStorage.setItem(cle, '1');
        } catch { /* stockage indisponible : on propose, quitte à le refaire */ }
        setDemandePvOpen(true);
      })
      .catch(() => { });
  }, [estTe, sinistre?.statut, sinistre?.sinistreTrackingId]);

  useEffect(() => {
    loadVictimes();
    loadPvs();
  }, [loadVictimes, loadPvs]);

  // Les parties adverses s'ajoutent depuis l'onglet Victimes, qui tient sa propre
  // liste : au retour sur Informations, le bloc « Assuré et parties impliquées »
  // doit montrer celles qu'on vient de saisir.
  const premierAffichageInfos = React.useRef(activeTab === 'infos');
  useEffect(() => {
    if (activeTab !== 'infos') return;
    if (premierAffichageInfos.current) { premierAffichageInfos.current = false; return; }
    loadVictimes();
  }, [activeTab, loadVictimes]);

  const onMettreEnContentieux = async (body) => {
    setSavingLitige(true);
    try {
      await axiosInstance.patch(`/v1/sinistres/${id}/contentieux`, body);
      toast.success(t('details.messages.misEnContentieux'));
      setContentieuxOpen(false);
      await loadSinistre();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('details.messages.erreurContentieux'));
    } finally {
      setSavingLitige(false);
    }
  };

  const onMettreEnArbitrage = async (body) => {
    setSavingLitige(true);
    try {
      await axiosInstance.patch(`/v1/sinistres/${id}/arbitrage`, body);
      toast.success(t('details.messages.misEnArbitrage'));
      setArbitrageOpen(false);
      await loadSinistre();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('details.messages.erreurArbitrage'));
    } finally {
      setSavingLitige(false);
    }
  };

  const onLeverLitige = () => {
    Modal.confirm({
      title: t('details.modales.leverLitige.titre'),
      content: t('details.modales.leverLitige.contenu'),
      okText: t('details.modales.confirmer'),
      cancelText: t('details.modales.annuler'),
      okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c' } },
      onOk: async () => {
        setSavingLitige(true);
        try {
          await axiosInstance.patch(`/v1/sinistres/${id}/sortir-litige`);
          toast.success(t('details.messages.litigeLeve'));
          await loadSinistre();
        } catch (e) {
          toast.error(e?.response?.data?.message || t('details.messages.erreurLeveeLitige'));
        } finally {
          setSavingLitige(false);
        }
      },
    });
  };


  // Validation de la déclaration (CSS/SE) - ne change plus le statut, juste le flag
  // declarationValidee (cf. paramètre VALIDATION_DECLARATION_ACTIVE côté backend).
  const onValiderDossier = async () => {
    setSavingValidation(true);
    try {
      const res = await axiosInstance.post(`/v1/sinistres/${id}/valider-declaration`);
      toast.success(res.data?.message || t('details.messages.declarationValidee'));
      setValidationModalOpen(false);
      await loadSinistre();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('details.messages.erreurValidation'));
    } finally {
      setSavingValidation(false);
    }
  };

  // TE : joint la fiche de l'homologue puis, si le dossier est en NOUVEAU, enregistre
  // la demande de garantie qu'elle porte (→ GARANTIE_ATTENTE_CONFIRMATION).
  const ficheEnregistreDemande = ['NOUVEAU', 'VALIDE'].includes(sinistre?.statut) && can(HAB.SINISTRE_GARANTIE);
  const onJoindreFicheHomologue = async (fichier) => {
    setSavingFiche(true);
    try {
      if (fichier) {
        const echecs = await joindrePiecesHomologue(sinistre, { fiche: fichier }, sinistre.dateDeclaration);
        if (echecs.length) throw new Error(t('details.messages.ficheNonClassee'));
      }
      if (ficheEnregistreDemande) {
        const res = await axiosInstance.post(`/v1/sinistres/${id}/demander-confirmation-garantie`);
        toast.success(res.data?.message || t('details.messages.demandeHomologueEnregistree'));
      } else {
        toast.success(t('details.messages.ficheJointe'));
      }
      setFicheModalOpen(false);
      await Promise.all([loadSinistre(), loadFicheHomologue()]);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || t('details.messages.erreurFiche'));
      loadFicheHomologue();
    } finally {
      setSavingFiche(false);
    }
  };

  // Demande de confirmation de garantie (NOUVEAU/VALIDE → GARANTIE_DEMANDEE ou
  // GARANTIE_ATTENTE_CONFIRMATION selon le type de sinistre).
  const onDemanderConfirmationGarantie = () => {
    Modal.confirm({
      title: t('details.modales.demandeGarantie.titre', { a: partieGarantie(sinistre).a }),
      content: t('details.modales.demandeGarantie.contenu', { destinataire: destinataireGarantie(sinistre) }),
      okText: t('details.modales.confirmer'),
      cancelText: t('details.modales.annuler'),
      okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c' } },
      onOk: async () => {
        try {
          const res = await axiosInstance.post(`/v1/sinistres/${id}/demander-confirmation-garantie`);
          toast.success(res.data?.message || t('details.messages.demandeGarantieEnregistree'));
          await loadSinistre();
        } catch (e) {
          toast.error(e?.response?.data?.message || t('details.messages.erreurDemandeGarantie'));
        }
      },
    });
  };

  // Confirmation de garantie (GARANTIE_DEMANDEE/ATTENTE_CONFIRMATION → GARANTIE_CONFIRMEE,
  // ou recours depuis GARANTIE_REJETEE/SANS_SUITE). Le backend exige la pièce
  // CONFIRMATION_GARANTIE en GED : on l'uploade d'abord, puis on confirme.
  const onConfirmerGarantie = async ({ pieceFile, ...payload }) => {
    setSavingGarantie(true);
    try {
      if (pieceFile) {
        const metadata = {
          titre: `Confirmation de garantie - ${sinistre.numeroSinistreLocal}`,
          typeDocument: 'CONFIRMATION_GARANTIE',
          dateDocument: dayjs().format('YYYY-MM-DD'),
          sinistreTrackingId: id,
        };
        const formData = new FormData();
        formData.append('file', pieceFile);
        formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        await axiosInstance.post('/v1/ged/documents', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      const res = await axiosInstance.patch(`/v1/sinistres/${id}/confirmer-garantie`, {
        garantieAcquise: true,
        ...payload,
      });
      setGarantieModalOpen(false);
      await loadSinistre();
      // On RESTE dans le dossier : la redirection d'office vers l'impression
      // faisait perdre l'onglet en cours, et il fallait re-naviguer pour
      // continuer l'instruction. L'attestation reste à un clic, dans le toast.
      // TE : le document à produire est la confirmation de garantie pour
      // l'homologue ; ET : l'attestation de déclaration.
      const doc = estSinistreTe(sinistre)
        ? { chemin: 'confirmation-garantie', libelle: t('details.messages.lienImprimerConfirmation') }
        : { chemin: 'attestation', libelle: t('details.messages.lienImprimerAttestation') };
      const ouvrirDoc = () => navigate(`/sinistres/${id}/${doc.chemin}`);
      toast.success(
        <span>
          {res.data?.message || t('details.messages.garantieConfirmee')} -{' '}
          <span style={{ textDecoration: 'underline', fontWeight: 600 }}
            onClick={ouvrirDoc} role="link" tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && ouvrirDoc()}>
            {doc.libelle}
          </span>
        </span>,
        { autoClose: 10000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.message || t('details.messages.erreurEnregistrement'));
    } finally {
      setSavingGarantie(false);
    }
  };

  // Rejet de la garantie (GARANTIE_DEMANDEE/ATTENTE_CONFIRMATION → SANS_SUITE), motif obligatoire.
  // Garantie non acquise : aucune pièce jointe n'est demandée au rejet.
  const onRejeterGarantie = async (payload) => {
    setSavingRejet(true);
    try {
      const res = await axiosInstance.patch(`/v1/sinistres/${id}/rejeter-garantie`, payload);
      toast.success(res.data?.message || t('details.messages.garantieRejetee'));
      setRejeterModalOpen(false);
      await loadSinistre();
      // Auto-navigate vers l'attestation de non-garantie (miroir de la confirmation),
      // document à produire en aval du rejet. Bouton « Retour au dossier » sur la page.
      navigate(`/sinistres/${id}/non-garantie`);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('details.messages.erreurRejetGarantie'));
    } finally {
      setSavingRejet(false);
    }
  };

  // Passage manuel à un statut (utilisé pour ATTENTE_PV, CLOTURE)
  const changerStatut = async (statut, successMsg, confirmer = false) => {
    try {
      await axiosInstance.patch(`/v1/sinistres/${id}/statut`, null, { params: { statut, confirmer } });
      toast.success(successMsg);
      await loadSinistre();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('details.messages.erreur'));
    }
  };

  /**
   * Validation d'une déclaration déposée depuis l'application (SOUMISE → NOUVEAU).
   *
   * À ne pas confondre avec `setValidationModalOpen`, la validation métier d'un
   * dossier NOUVEAU/VALIDE : celle-ci ne fait que faire entrer une déclaration
   * mobile brute dans le flux. Elle enchaîne donc sur le formulaire de modification -
   * la déclaration mobile est simplifiée, le dossier créé est partiel.
   *
   * Le classement sans suite, l'autre issue possible, se fait depuis la file d'attente
   * (`/mobile/declarations`) : il exige un motif écrit, mal servi par une Modal.confirm.
   */
  const onValiderDeclarationMobile = () => {
    Modal.confirm({
      title: t('details.modales.validationMobile.titre'),
      content: t('details.modales.validationMobile.contenu'),
      okText: t('details.modales.validationMobile.valider'),
      cancelText: t('details.modales.annuler'),
      okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c' } },
      onOk: async () => {
        try {
          await mobileService.validerDeclaration(id);
          toast.success(t('details.messages.declarationValidee'));
          navigate(`/sinistres/${id}/modifier`);
        } catch (e) {
          toast.error(e?.response?.data?.message || t('details.messages.validationImpossible'));
        }
      },
    });
  };

  const onCloturer = async () => {
    // Dossier classé sans suite : pas de flux d'indemnisation, la soldabilité
    // n'a pas de sens - confirmation directe de la clôture définitive.
    if (sinistre.statut === 'SANS_SUITE') {
      Modal.confirm({
        title: t('details.modales.cloture.titre'),
        content: t('details.modales.cloture.sansSuite'),
        okText: t('details.modales.cloture.cloturer'),
        cancelText: t('details.modales.annuler'),
        okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c' } },
        onOk: () => changerStatut('CLOTURE', t('details.messages.dossierCloture'), true),
      });
      return;
    }

    let sold = null;
    try {
      const r = await axiosInstance.get(`/v1/sinistres/${id}/soldabilite`);
      sold = r?.data?.data;
    } catch { /* soldabilité indisponible : on retombe sur une confirmation générique */ }

    const fmt = (c, prefixe) => c ? `${prefixe} ${c.soldes}/${c.total}` : '';
    const soldable = sold?.soldable ?? false;
    const resume = sold ? `${fmt(sold.corporel, t('details.modales.cloture.corporel'))} · ${fmt(sold.materiel, t('details.modales.cloture.materiel'))}` : '';
    const nonSoldes = sold ? [...(sold.corporel?.nonSoldes || []), ...(sold.materiel?.nonSoldes || [])] : [];

    Modal.confirm({
      title: t('details.modales.cloture.titre'),
      content: !sold
        ? t('details.modales.cloture.confirmerCloture')
        : soldable
          ? t('details.modales.cloture.tousSoldes', { resume })
          : (nonSoldes.length ? t('details.modales.cloture.nonSoldesAvecListe', {
              n: nonSoldes.length, resume, liste: `${nonSoldes.slice(0, 6).join(', ')}${nonSoldes.length > 6 ? '…' : ''}`,
            }) : t('details.modales.cloture.nonSoldesSansListe', { n: nonSoldes.length, resume })),
      okText: t('details.modales.cloture.cloturer'),
      cancelText: t('details.modales.annuler'),
      okButtonProps: { style: { background: '#1a6b3c', borderColor: '#1a6b3c' } },
      onOk: () => changerStatut('CLOTURE', t('details.messages.dossierCloture'), true),
    });
  };

  useEffect(() => {
    const handler = (e) => {
      if (e?.detail) setActiveTab(e.detail);
    };
    window.addEventListener('cb:goto-tab', handler);
    return () => window.removeEventListener('cb:goto-tab', handler);
  }, []);

  const evenements = useMemo(
    () => buildTimeline(sinistre, tracabilite, t),
    [sinistre, tracabilite, t],
  );

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    </Layout>
  );

  if (!sinistre) return (
    <Layout>
      <Empty description={t('details.messages.sinistreIntrouvable')} />
    </Layout>
  );

  const statut = STATUT_MAP[sinistre.statut]
    ? { ...STATUT_MAP[sinistre.statut], label: libelleStatutGarantie(sinistre.statut, sinistre.typeSinistre) }
    : { label: sinistre.statut, color: 'default' };
  // Provenance : dossier issu de la reprise historique (import Excel 2021-2026).
  const repriseTooltip = [
    sinistre.sourceReprise && t('details.entete.reprise.importeDe', { source: sinistre.sourceReprise }),
    sinistre.referenceOrigine && t('details.entete.reprise.refOrigine', { reference: sinistre.referenceOrigine }),
  ].filter(Boolean).join(' · ') || t('details.entete.reprise.dossierIssuReprise');
  // La déclaration reste modifiable tant que le dossier n'est pas encore « mûr »
  // (aligné avec StatutSinistre.isEditableDeclaration() côté backend).
  /* Un seul point d'entree pour changer d'onglet, appele depuis les deux niveaux
     de la barre. Les onglets Offres et Expertises consomment le `victimes`
     page-level, charge au montage : VictimesTab gere son propre etat sans notifier
     le parent, on rafraichit donc ici pour ne pas afficher une liste perimee. */
  const changerOnglet = (cle) => {
    setActiveTab(cle);
    if (cle === 'offres' || cle === 'expertises') loadVictimes();
  };

  /**
   * Une declaration mobile SOUMISE n'est pas encore un dossier : elle attend d'etre
   * validee ou classee sans suite. La machine d'etats backend le dit sans ambiguite -
   * `autoriser(SOUMISE, NOUVEAU, SANS_SUITE)`, deux issues, pas une de plus.
   *
   * Le front, lui, proposait presque tout le workflow : enregistrer un PV, ouvrir une
   * reclamation, mettre en contentieux, changer le statut a la main. Autant d'actions
   * que le backend refuse ensuite - l'agent decouvrait l'interdit APRES avoir rempli
   * un formulaire. Pire, c'est exactement le dossier ou l'on soupconne une fraude :
   * il ne doit rien pouvoir declencher tant que personne ne l'a controle.
   */
  const enAttenteValidation = sinistre.statut === 'SOUMISE';
  /* L'origine du dossier se lit sur `canal`, JAMAIS sur le statut. SOUMISE a longtemps
     été propre au canal mobile ; depuis la corbeille, une déclaration saisie au guichet
     sans rédacteur y naît aussi. Déduire « mobile » du statut affichait « déclaré par un
     usager depuis l'application » sur des dossiers saisis par un agent, et proposait une
     validation mobile que le backend refuse pour un dossier WEB. */
  const estMobile = estCanalMobile(sinistre.canal);

  const canEdit = !isDossierCloture(sinistre.statut);
  const canOpenLitige = !['CONTENTIEUX', 'ARBITRAGE', 'CLOTURE'].includes(sinistre.statut);
  const canLeverLitige = ['CONTENTIEUX', 'ARBITRAGE'].includes(sinistre.statut);

  // Validation de la déclaration : un bouton visible tant qu'elle n'est pas faite.
  // Elle était rangée dans « ⋯ Actions » et personne ne la trouvait ; le statut ne
  // bougeant pas à la validation, seul `declarationValidee` dit si elle est faite.
  // Jusqu'à la réponse sur la garantie : un TE pris en charge avec sa fiche passe
  // directement en attente de confirmation, sans jamais s'arrêter en NOUVEAU.
  // Dès SOUMISE pour le guichet : une déclaration saisie sans rédacteur attend son
  // affectation, et la valider n'en dépend pas. Pas pour le mobile, dont la SOUMISE
  // se lève par « Valider la déclaration mobile » : deux « Valider » se confondraient.
  const peutValiderDeclaration = (
    ['NOUVEAU', 'VALIDE', 'GARANTIE_DEMANDEE', 'GARANTIE_ATTENTE_CONFIRMATION'].includes(sinistre.statut)
    || (sinistre.statut === 'SOUMISE' && !estMobile)
  )
    && !sinistre.declarationValidee
    && can(HAB.SINISTRE_UPDATE);

  // ─ Action contextuelle principale (un seul bouton primaire selon le statut) ─
  /* « Gérer les pièces » : une seule personne à indemniser, déjà dotée d'un dossier →
     droit dans son dossier ; sinon la liste des dossiers DE CE SINISTRE, qui signale les
     personnes encore sans dossier. */
  const gererLesPieces = () => {
    const personnes = maturite?.victimes || [];
    if (personnes.length === 1 && personnes[0].dossierTrackingId) {
      navigate(`/reclamations/${personnes[0].dossierTrackingId}`);
    } else {
      navigate(`/reclamations?sinistreId=${id}`);
    }
  };

  const primaryAction = (() => {
    const s = sinistre.statut;
    // Actions garantie : réservées aux utilisateurs habilités (le backend exige
    // SINISTRE_GARANTIE sur demander/confirmer/rejeter-garantie).
    const peutGererGarantie = can(HAB.SINISTRE_GARANTIE);
    // Une déclaration mobile non validée précède tout le reste : tant qu'elle est
    // SOUMISE, aucune action du workflow n'a de sens sur un dossier partiel.
    if (s === 'SOUMISE' && estMobile && can(HAB.SINISTRE_VALIDER_DECLARATION)) return {
      label: t('details.actions.validerDeclarationMobile'), icon: <MobileOutlined />,
      onClick: onValiderDeclarationMobile,
    };
    // Une déclaration du guichet sort de l'antichambre par l'AFFECTATION, pas par une
    // validation. Sans ce retour, le dossier tombait sur l'action par défaut
    // « Enregistrer un PV », que le backend refuse sur un dossier SOUMISE.
    // Règle du 2026-09-14 : l'agent se sert lui-même ; le responsable, lui, distribue.
    // Les deux actions vivent dans l'onglet Affectation (confirmation, trajectoire).
    if (s === 'SOUMISE') {
      if (estMobile) return null;
      if (can(HAB.SINISTRE_AUTO_AFFECTATION)) return {
        label: t('details.actions.saffecter'), icon: <UserSwitchOutlined />,
        onClick: () => setActiveTab('affectation'),
      };
      return can(HAB.SINISTRE_ASSIGN) ? {
        label: t('details.actions.affecterDossier'), icon: <UserSwitchOutlined />,
        onClick: () => setActiveTab('affectation'),
      } : null;
    }
    // TE : la demande de confirmation vient du bureau homologue, par sa fiche. Le
    // bureau ne la demande pas, il enregistre celle qu'il a reçue.
    if ((s === 'NOUVEAU' || s === 'VALIDE') && peutGererGarantie && estTe) return ficheHomologue === null ? null : {
      label: ficheHomologue ? t('details.actions.demanderGarantieCompagnie') : t('details.actions.joindreFicheHomologue'),
      icon: <PaperClipOutlined />,
      onClick: () => setFicheModalOpen(true),
    };
    if ((s === 'NOUVEAU' || s === 'VALIDE') && peutGererGarantie) return {
      label: t('details.actions.demanderGarantieBureau'), icon: <SafetyCertificateOutlined />,
      onClick: onDemanderConfirmationGarantie,
    };
    if ((s === 'GARANTIE_DEMANDEE' || s === 'GARANTIE_ATTENTE_CONFIRMATION') && peutGererGarantie) return {
      // La confirmation revient de celui à qui on l'a demandée.
      label: t('details.actions.confirmationRecue', { de: partieGarantie(sinistre).de }), icon: <SafetyCertificateOutlined />,
      onClick: () => setGarantieModalOpen(true),
    };
    // Recours = réouverture d'un dossier sans suite : en plus de SINISTRE_GARANTIE,
    // exige SINISTRE_REOUVRIR (miroir du contrôle service côté backend).
    if ((s === 'GARANTIE_REJETEE' || (s === 'SANS_SUITE' && sinistre.garantieAcquise === false))
      && peutGererGarantie && can(HAB.SINISTRE_REOUVRIR)) return {
        label: t('details.actions.recoursConfirmerGarantie'), icon: <SafetyCertificateOutlined />,
        onClick: () => setGarantieModalOpen(true),
      };
      /* TE : ce que le bureau produit apres la confirmation, c'est la lettre de
         CONFIRMATION DE GARANTIE adressee a l'homologue - celui-la meme qui avait
         pose la question. L'attestation de declaration, elle, est le document du
         dossier ET, remis cote togolais. */
    if (s === 'GARANTIE_CONFIRMEE') return {
      label: estTe ? t('details.actions.imprimerConfirmationGarantie') : t('details.actions.imprimerAttestation'),
      icon: <PrinterOutlined />,
      onClick: async () => {
        /* L'impression n'est pas une étape du workflow : la garantie est
           confirmée, l'instruction peut démarrer. On ouvre donc l'instruction
           (ATTENTE_PV) puis on affiche l'attestation. Si la transition échoue
           (habilitation, statut déjà avancé), on imprime quand même : la page
           d'attestation ne dépend pas du statut, elle reste accessible ensuite. */
        try {
          await axiosInstance.patch(`/v1/sinistres/${id}/statut`, null, {
            params: { statut: 'ATTENTE_PV' },
            skipErrorToast: true,
          });
          await loadSinistre();
          toast.success(t('details.messages.instructionOuverte'));
        } catch (e) {
          toast.warning(
            t('details.messages.attestationResteImprimable', { raison: e?.response?.data?.message || t('details.messages.statutInchange') })
          );
        }
        navigate(`/sinistres/${id}/${estTe ? 'confirmation-garantie' : 'attestation'}`);
      },
    };
    if (s === 'GARANTIE_NON_ACQUISE') return {
      label: t('details.actions.imprimerNonGarantie'), icon: <StopOutlined />, danger: true,
      onClick: () => navigate(`/sinistres/${id}/non-garantie`),
    };
    if (s === 'ATTENTE_PV') {
      // TE : le PV est dressé à l'étranger, il se DEMANDE au bureau homologue -
      // l'enregistrement viendra quand il arrivera.
      if (estTe && !pvs.length) {
        /* La demande partie, la refaire n'avance à rien : le dossier attend
           l'homologue, et ce qui le fait bouger est l'arrivée du PV. Le courriel
           envoyé reste consultable dans les courriers du dossier. */
        if (demandePvEnvoyee) return {
          label: t('details.actions.enregistrerPvRecu'), icon: <FileTextOutlined />,
          onClick: () => setPvCreateOpen(true),
        };
        return {
          label: t('details.actions.demanderPvHomologue'), icon: <MailOutlined />,
          onClick: () => setDemandePvOpen(true),
        };
      }
      return {
        label: pvs.length ? t('details.actions.voirPv') : t('details.actions.enregistrerPv'), icon: <FileTextOutlined />,
        onClick: () => pvs.length ? setPvDetailId(pvs[0].pvTrackingId) : setPvCreateOpen(true),
      };
    }
    // Le bouton ouvre la RC de l'assuré. Celle des autres parties s'établit sur leur
    // fiche, dans l'onglet Victimes (part de responsabilité, négociation des adverses).
    // Il ne recopie plus le taux du dossier sur les victimes : depuis que ce taux
    // désigne la faute de la personne, il les aurait déclarées responsables.
    if (s === 'ATTENTE_RC') return {
      label: t('details.actions.etablirRc'), icon: <SafetyOutlined />,
      onClick: () => setRcAssureOpen(true),
    };
    // Pièces toutes reçues, seule la consolidation manque : « Gérer les pièces » renvoyait
    // vers des pièces déjà complètes, et le dossier semblait bloqué sans raison.
    const enAttente = (maturite?.victimes || []).filter((v) => !v.estMur);
    if (s === 'ATTENTE_PIECES_DE_RECLAMATION' && enAttente.length > 0
      && enAttente.every((v) => etatMaturitePersonne(v) === 'CONSOLIDATION')) return {
        label: t('details.actions.saisirDateConsolidation'), icon: <ClockCircleOutlined />,
        onClick: () => setConsolidationOpen(true),
      };
    if (s === 'GARANTIE_CONFIRMEE' || s === 'ATTENTE_PIECES_DE_RECLAMATION') return {
      label: t('details.actions.gererPieces'), icon: <FolderOpenOutlined />,
      onClick: gererLesPieces,
    };
    // TE : le sens est inverse - l'offre vient de l'homologue, la compagnie paie par
    // chèque, le BNCB le transmet à l'homologue. Chaque bouton dit qui on attend.
    if (s === 'MUR' && estTe) return {
      label: t('details.actions.enregistrerOffreHomologue'), icon: <FileDoneOutlined />,
      onClick: () => setActiveTab('offres'),
    };
    if (s === 'MUR') return {
      label: t('details.actions.calculerOffre'), icon: <SafetyCertificateOutlined />,
      // Dans l'onglet Offres du dossier, pas sur la page transverse « Établir une
      // offre » : l'agent est déjà dans son dossier, l'en sortir lui faisait tout
      // re-chercher.
      onClick: () => setActiveTab('offres'),
    };
    // Offre en cours : l'étape suivante se fait dans l'onglet Offres, victime par victime.
    // Sans ces cas, le dossier retombait sur l'action par défaut « Enregistrer un PV ».
    if (s === 'OFFRE_EFFECTUE') return {
      label: t('details.actions.transmettreOffre', { a: partieGarantie(sinistre).a }), icon: <SendOutlined />,
      onClick: () => setActiveTab('offres'),
    };
    if (s === 'ATTENTE_OFFRE') return {
      label: t('details.actions.enregistrerReponse', { de: partieGarantie(sinistre).de }), icon: <FileDoneOutlined />,
      onClick: () => setActiveTab('offres'),
    };
    if (s === 'OFFRE_RECUE') return {
      label: t('details.actions.enregistrerDecisionVictime'), icon: <FileDoneOutlined />,
      onClick: () => setActiveTab('offres'),
    };
    if (s === 'REJET') return {
      label: t('details.actions.revoirOffre'), icon: <SafetyCertificateOutlined />,
      onClick: () => setActiveTab('offres'),
    };
    if (s === 'REOUVERT') return {
      label: t('details.actions.gererPieces'), icon: <FolderOpenOutlined />,
      onClick: gererLesPieces,
    };
    if (estTe && ['ACCORD', 'BAP', 'ATTENTE_CHEQUE'].includes(s)) return {
      label: t('details.actions.enregistrerChequeCompagnie'), icon: <DollarOutlined />,
      onClick: () => setActiveTab('offres'),
    };
    if (estTe && s === 'CHEQUE_RECU') return {
      label: t('details.actions.transmettreChequeHomologue'), icon: <SendOutlined />,
      onClick: () => setActiveTab('offres'),
    };
    if (estTe && s === 'PARTIELLEMENT_PAYE') return {
      label: t('details.actions.suivreQuittancesHomologue'), icon: <FileDoneOutlined />,
      onClick: () => setActiveTab('offres'),
    };
    if (s === 'ACCORD') return {
      label: t('details.actions.emettreBap'), icon: <FileDoneOutlined />,
      onClick: () => navigate(`/comptabilite?sinistreId=${id}`),
    };
    if (['BAP', 'ATTENTE_CHEQUE', 'CHEQUE_RECU', 'PARTIELLEMENT_PAYE'].includes(s)) return {
      label: t('details.actions.reglerPaiement'), icon: <DollarOutlined />,
      onClick: () => navigate(`/paiements?sinistreId=${id}`),
    };
    if (s === 'SOLDE' && can(HAB.SINISTRE_CLOTURER)) return {
      label: t('details.actions.cloturer'), icon: <CheckCircleOutlined />,
      onClick: onCloturer,
    };
    // Par défaut : enregistrement de PV (action courante) - seulement avant la
    // réclamation. Une fois le dossier mûr, un PV s'ajoute encore par « Actions », mais
    // ce n'est plus l'étape du dossier : l'afficher après « Offre effectuée » égarait.
    // Bloqué tant que le sinistre n'est pas validé (NOUVEAU).
    if (s === 'NOUVEAU') return null;
    if (['SOLDE', 'CLOTURE', 'SANS_SUITE', 'CONTENTIEUX', 'ARBITRAGE'].includes(s)) return null;
    return {
      label: t('details.actions.enregistrerPv'), icon: <PlusOutlined />,
      onClick: () => setPvCreateOpen(true),
    };
  })();

  // Dropdown ⋯ Actions (enrichi)
  const actionItems = [
    ...(canEdit ? [
      {
        key: 'edit', icon: <EditOutlined />, label: t('details.actions.modifierDeclaration'),
        onClick: () => navigate(`/sinistres/${id}/modifier`)
      },
    ] : []),
    ...(['GARANTIE_DEMANDEE', 'GARANTIE_ATTENTE_CONFIRMATION'].includes(sinistre.statut) && can(HAB.SINISTRE_GARANTIE) ? [
      {
        key: 'rejeter', icon: <StopOutlined />, danger: true, label: t('details.actions.rejeterGarantie'),
        onClick: () => setRejeterModalOpen(true)
      },
    ] : []),
    ...(sinistre.statut !== 'NOUVEAU' && !enAttenteValidation ? [
      {
        key: 'pv-add', icon: <PlusOutlined />, label: t('details.actions.enregistrerPv'),
        onClick: () => setPvCreateOpen(true)
      },
    ] : []),
    ...(enAttenteValidation ? [] : [{
      key: 'reclam', icon: <FolderOpenOutlined />, label: t('details.actions.ouvrirReclamation'),
      onClick: () => navigate(`/reclamations?sinistreId=${id}`)
    }]),
    ...(enAttenteValidation ? [] : [
      {
        key: 'indem', icon: <SafetyCertificateOutlined />, label: t('details.actions.voirIndemnisation'),
        onClick: () => navigate(`/indemnisation?sinistreId=${id}`)
      },
      {
        key: 'changer-statut', icon: <SwapOutlined />, label: t('details.actions.changerStatut'),
        onClick: () => setStatutModalOpen(true)
      },
      { type: 'divider' },
      ...(canOpenLitige ? [
        {
          key: 'contentieux', icon: <ExclamationCircleOutlined />, danger: true,
          label: t('details.actions.mettreEnContentieux'), onClick: () => setContentieuxOpen(true)
        },
        {
          key: 'arbitrage', icon: <SafetyOutlined />,
          label: t('details.actions.mettreEnArbitrage'), onClick: () => setArbitrageOpen(true)
        },
      ] : []),
      ...(canLeverLitige ? [
        {
          key: 'lever-litige', icon: <UnlockOutlined />,
          label: t('details.actions.leverLitige'), onClick: onLeverLitige
        },
      ] : []),
    ]),
    ...(['SOLDE', 'SANS_SUITE'].includes(sinistre.statut) && can(HAB.SINISTRE_CLOTURER) ? [
      {
        key: 'cloturer', icon: <CheckCircleOutlined />,
        label: t('details.actions.cloturerDossier'), onClick: onCloturer
      },
    ] : []),
  ];

  return (
    <Layout title={t('details.entete.titrePage', { numero: sinistre.numeroSinistreLocal })}>
      {/*  Le dossier est enveloppé dans DiscussionDossier : la conversation interne
          occupe un rail permanent à droite, qui POUSSE le contenu au lieu de le
          recouvrir. Replié, il porte les avatars des participants et le compte de
          non-lus - c'est ce qui fait qu'on sait qu'une discussion existe sans
          l'ouvrir. Le composant fournit aussi le contexte d'ancrage consommé par
          les pastilles posées sur les lignes du dossier (victimes, offres…).  */}
      <DiscussionDossier entiteType={ENTITE.SINISTRE} entiteId={sinistre.sinistreTrackingId}>
        {/*  Retour + header  */}
        {/* `position: relative` ancre la banderole des dossiers non affectes.
          Le decoupage reste dans son propre conteneur : un overflow pose ici
          rognerait les menus d'action loges dans cet en-tete. */}
        <div className="cb-page-header" style={{ position: 'relative' }}>
          <RubanNonAffecte sinistre={sinistre} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/sinistres')} type="text" style={{ flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <h1 className="cb-page-title">{sinistre.numeroSinistreLocal}</h1>
              <p className="cb-page-sub">
                {t('details.entete.accidentDu', { date: formatDate(sinistre.dateAccident) })}
                {sinistre.lieuAccident ? ` - ${sinistre.lieuAccident}` : ''}
                {sinistre.paysEmetteurLibelle ? t('details.entete.vehicule', { pays: sinistre.paysEmetteurLibelle }) : ''}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {canEdit && (
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/sinistres/${id}/modifier`)}
                style={{ borderRadius: 8 }}
              >
                {t('details.entete.modifier')}
              </Button>
            )}
            <Button
              onClick={() => setActiveTab('documents')}
              style={{ borderRadius: 8 }}
              icon={<FolderOpenOutlined />}
            >
              OssanGED
            </Button>

            {peutValiderDeclaration && (
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => setValidationModalOpen(true)}
                style={{ borderRadius: 8, fontWeight: 600, color: '#1a6b3c', borderColor: '#1a6b3c' }}
              >
                {t('details.entete.validerDeclaration')}
              </Button>
            )}

            {/* Lier / détacher ne vit plus ici : le geste a rejoint l'onglet « Lier des
              dossiers sinistres », où le formulaire et les dossiers déjà groupés se
              voient d'emblée. Un bouton de barre ouvrant une fenêtre ne montrait ni
              l'un ni l'autre tant qu'on ne l'avait pas trouvé. */}

            {/* Action principale contextuelle au statut courant - pour faire avancer le dossier
              sans avoir à retourner dans la liste des sinistres */}
            {primaryAction && (
              <Button
                type="primary"
                icon={primaryAction.icon}
                danger={primaryAction.danger}
                onClick={primaryAction.onClick}
                style={primaryAction.danger
                  ? { borderRadius: 8, fontWeight: 600 }
                  : { background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8, fontWeight: 600 }}
              >
                {primaryAction.label}
              </Button>
            )}

            {actionItems.length > 0 && (
              <Dropdown trigger={['click']} menu={{ items: actionItems }}>
                <Button icon={<MoreOutlined />} style={{ borderRadius: 8 }}>
                  {t('details.entete.actions')}
                </Button>
              </Dropdown>
            )}
          </div>
        </div>

        {/* Ce qu'il faut savoir du dossier avant d'ouvrir un onglet, en une rangee de
          colonnes plutot qu'en etiquettes de couleur. Chaque case annonce ce qu'elle
          contient, et une case vide se lit aussi bien qu'une case pleine - ce qu'une
          etiquette absente ne permettait pas. */}
        <ResumeEntete
          style={{ marginBottom: 16 }}
          colonnes={[
            { cle: 'statut', libelle: t('details.entete.resume.statut'), valeur: statut.label },
            {
              cle: 'type',
              libelle: t('details.entete.resume.type'),
              valeur: estTe ? 'TE' : 'ET',
              infobulle: estTe
                ? t('details.entete.resume.teComplet')
                : t('details.entete.resume.etComplet'),
            },
            {
              cle: 'rc',
              libelle: t('details.entete.resume.positionRc'),
              valeur: POSITION_RC_MAP[sinistre.positionRc]?.label,
            },
            {
              cle: 'declaration',
              libelle: t('details.entete.resume.declaration'),
              valeur: sinistre.declarationValidee
                ? t('details.entete.resume.validee')
                : t('details.entete.resume.aValider'),
            },
            {
              cle: 'source',
              libelle: t('details.entete.resume.source'),
              valeur: CANAL_MAP[sinistre.canal || 'WEB']?.label,
            },
            /* La colonne « Origine » n'apparait que sur un dossier repris : sur un
               dossier saisi elle dirait l'evidence, et c'est l'infobulle - fichier
               d'origine, reference source - qui fait son interet. */
            sinistre.repriseHistorique && {
              cle: 'origine',
              libelle: t('details.entete.resume.origine'),
              valeur: t('details.entete.resume.reprise'),
              infobulle: repriseTooltip,
            },
            {
              cle: 'liaison',
              libelle: t('details.entete.resume.sinistreLie'),
              valeur: sinistre.liaisonTrackingId ? sinistre.numeroLiaison : null,
              infobulle: sinistre.liaisonTrackingId && can(HAB.SINISTRE_LIER)
                ? t('details.entete.voirLiaison') : undefined,
              accent: '#1a6b3c',
              onClick: can(HAB.SINISTRE_LIER) && sinistre.liaisonTrackingId
                ? () => navigate('/sinistres-lies') : undefined,
            },
          ]}
        />

        {/* La banniere « Prochaine etape » a ete retiree : elle reprenait mot pour mot
          le libelle du bouton principal situe quarante pixels plus haut, et son
          « Y aller » declenchait exactement le meme handler. Deux fois la meme
          action, dont une dans un cadre vert de 3 px - c'est ce qui donnait a
          l'en-tete son air encombre. Le bouton primaire suffit. */}

        {/* SOUMISE precede le workflow : aucune etape du stepper n'est franchie, et un
          stepper entierement vide se lit comme une panne plutot que comme un debut.
          Le bandeau d'alerte dedie, plus bas, dit ou en est le dossier. */}
        {sinistre.statut !== 'SOUMISE' && <SinistreStatusStepper sinistre={sinistre} />}

        {/*  Maturité pondérée par victime : 1 victime mûre = 1/N de la maturité  */}
        {/* Ce qui bloque le dossier, et qui doit agir. Le statut dit QUOI on attend,
          jamais DE QUI : sans cette distinction, un dossier dont le chèque est prêt
          mais dont la victime est injoignable est indiscernable d'un dossier laissé
          traîner - et c'est l'agent qui en porte le retard. */}
        {!['CLOTURE', 'SANS_SUITE'].includes(sinistre.statut) && (
          <BandeauAttentes
            sinistreId={id}
            victimes={victimes}
            onChange={() => setJournalTick((t) => t + 1)}
          />
        )}

        {maturite?.nbVictimes > 0 && !['CLOTURE', 'SANS_SUITE'].includes(sinistre.statut) && (
          <div style={{
            margin: '16px 0',
            padding: '14px 20px',
            borderRadius: 12,
            background: '#fff',
            border: '1px solid #e5e7eb',
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FileDoneOutlined style={{ fontSize: 20, color: maturite.estMur ? '#166534' : '#1a6b3c' }} />
              <span style={{ fontWeight: 700, fontSize: 14 }}>{t('details.maturite.titre')}</span>
              <Tag color={maturite.estMur ? 'success' : 'gold'} style={{ borderRadius: 20, fontWeight: 600 }}>
                {t('details.maturite.victimesMures', { count: maturite.nbVictimes, progression: maturite.progression })}
              </Tag>
            </div>
            {/* Un segment par personne à indemniser, rempli selon où elle en est. Une
              barre au pourcentage de victimes mûres restait à 0 % tant qu'aucune ne
              l'était tout à fait : des pièces reçues ou une victime qui n'attend plus
              que sa consolidation ne faisaient rien bouger. */}
            <div style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {maturite.victimes.map((v) => {
                  const etat = etatMaturitePersonne(v);
                  const remplissage = etat === 'PIECES' && v.nbPiecesRequises > 0
                    ? Math.round((100 * v.nbPiecesObligatoiresRecues) / v.nbPiecesRequises)
                    : etat === 'SANS_DOSSIER' ? 0 : 100;
                  return (
                    <Tooltip key={v.victimeTrackingId}
                      title={`${v.victimeNomComplet || t('details.maturite.victime')}${v.estAdversaire ? t('details.maturite.partieAdverseParentheses') : ''} - ${t(ETATS_MATURITE[etat].libelle)}`
                        + (v.dossierTrackingId ? t('details.maturite.piecesObligatoiresSuffixe', { recues: v.nbPiecesObligatoiresRecues, requises: v.nbPiecesRequises }) : '')}>
                      <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#e5e7eb', overflow: 'hidden' }}>
                        <div style={{ width: `${remplissage}%`, height: '100%', background: ETATS_MATURITE[etat].couleur }} />
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                {Object.entries(ETATS_MATURITE)
                  .map(([etat, cfg]) => [maturite.victimes.filter((v) => etatMaturitePersonne(v) === etat).length, cfg.libelle])
                  .filter(([n]) => n > 0)
                  .map(([n, libelle]) => `${n} ${t(libelle)}`)
                  .join(' · ')}
              </span>
            </div>
            {/* Une pastille par victime, en pleine largeur et en permanence : sur un
              dossier a cinq victimes cela faisait cinq phrases longues empilees sous
              l'en-tete, alors que l'onglet Victimes dit deja la meme chose en detail.
              Replie par defaut ; la barre de progression au-dessus porte l'essentiel. */}
            <Button
              type="link"
              size="small"
              onClick={() => setMaturiteDetail((v) => !v)}
              style={{ padding: 0, height: 'auto', color: '#1a6b3c', fontWeight: 600 }}
            >
              {maturiteDetail ? t('details.maturite.masquerDetail') : t('details.maturite.afficherDetail')}
            </Button>
            {/* Pourquoi le dossier n'avance pas. Le serveur le calculait depuis le
              début sans jamais le dire : le dossier restait bloqué avant MUR sans
              qu'aucun écran n'explique l'arrêt, et l'agent en concluait qu'il
              fallait forcer le statut. Une seule ligne, celle qui bloque le plus :
              cinq motifs empilés ne se lisent pas. */}
            {!maturite.estMur && motifBlocageMaturite(maturite) && (
              <div style={{
                width: '100%', marginTop: 2, padding: '8px 12px', borderRadius: 8,
                background: '#fffbeb', color: '#78350f', fontSize: 12.5, lineHeight: 1.5,
                display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap',
              }}>
                <ClockCircleOutlined style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 200 }}>{motifBlocageMaturite(maturite).texte}</span>
                {/* Le motif dit quoi faire ; le lien mène là où ça se fait. */}
                {motifBlocageMaturite(maturite).etat === 'CONSOLIDATION' && (
                  <Button type="link" size="small" onClick={() => setConsolidationOpen(true)}
                    style={{ padding: 0, height: 'auto', color: '#7c3aed', fontWeight: 600 }}>
                    {t('details.maturite.saisirDateConsolidation')}
                  </Button>
                )}
                {['SANS_DOSSIER', 'PIECES'].includes(motifBlocageMaturite(maturite).etat) && (
                  <Button type="link" size="small" onClick={gererLesPieces}
                    style={{ padding: 0, height: 'auto', color: '#1a6b3c', fontWeight: 600 }}>
                    {t('details.maturite.gererPieces')}
                  </Button>
                )}
              </div>
            )}
            <div style={{
              display: maturiteDetail ? 'flex' : 'none',
              gap: 6, flexWrap: 'wrap', width: '100%',
            }}>
              {maturite.victimes.map(v => {
                const etat = etatMaturitePersonne(v);
                const precisions = [
                  v.estAdversaire && t('details.maturite.partieAdverse'),
                  { CORPOREL: t('details.maturite.corporel'), MATERIEL: t('details.maturite.materiel'), MIXTE: t('details.maturite.mixte') }[v.typeDommage],
                ].filter(Boolean).join(', ');
                return (
                  <Tooltip
                    key={v.victimeTrackingId}
                    title={v.motifBlocage
                      || t('details.maturite.piecesObligatoires', { recues: v.nbPiecesObligatoiresRecues, requises: v.nbPiecesRequises })
                      + (v.consolidationRequise ? t('details.maturite.victimeConsolidee') : '')}
                  >
                    <Tag color={ETATS_MATURITE[etat].tag} style={{ borderRadius: 20, cursor: 'default' }}>
                      {v.victimeNomComplet || v.numeroDossier}
                      {precisions ? ` (${precisions})` : ''}
                      {' - '}
                      {t(ETATS_MATURITE[etat].libelle)}
                    </Tag>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}

        {/*  Déclaration mobile non encore validée par un agent  */}
        {sinistre.statut === 'SOUMISE' && estMobile && (
          <Alert
            showIcon
            type="warning"
            icon={<MobileOutlined />}
            style={{ borderRadius: 12, margin: '16px 0' }}
            message={t('details.alertes.mobile.titre')}
            description={
              <span>
                {t('details.alertes.mobile.texte')}
                {!can(HAB.SINISTRE_VALIDER_DECLARATION) && t('details.alertes.mobile.habilitationRequise')}
              </span>
            }
            action={can(HAB.SINISTRE_VALIDER_DECLARATION) ? (
              <Button size="small" type="primary" onClick={onValiderDeclarationMobile}
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
                {t('details.alertes.mobile.validerCompleter')}
              </Button>
            ) : null}
          />
        )}

        {/*  Courriel de demande de confirmation de garantie : à qui, quand, ou pourquoi il n'est pas parti  */}
        <DemandeGarantieBandeau sinistre={sinistre} />
        <ConsolidationModal
          open={consolidationOpen}
          onClose={() => setConsolidationOpen(false)}
          personnes={maturite?.victimes || []}
          onEnregistre={() => { loadMaturite(); loadSinistre(); }}
        />
        <RcAssureModal
          open={rcAssureOpen}
          onClose={() => setRcAssureOpen(false)}
          sinistre={sinistre}
          peutModifier={can(HAB.SINISTRE_UPDATE)}
          onEnregistre={rafraichirSinistre}
          onVoirParties={() => setActiveTab('victimes')}
        />

        {/*  TE : fiche de déclaration du bureau homologue absente  */}
        {ficheHomologue === false && ficheHomologueAttendue(sinistre) && (
          <Alert
            showIcon
            type="warning"
            icon={<PaperClipOutlined />}
            style={{ borderRadius: 12, margin: '16px 0' }}
            message={t('details.alertes.ficheHomologue.titre')}
            description={t('details.alertes.ficheHomologue.texte')}
            action={can(HAB.GED_CREATE) ? (
              <Button size="small" type="primary" onClick={() => setFicheModalOpen(true)}
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
                {t('details.alertes.ficheHomologue.joindre')}
              </Button>
            ) : null}
          />
        )}

        {/*  Garantie : accident hors période de couverture  */}
        {sinistre.horsPeriodeCouverture && sinistre.statut !== 'CLOTURE' && (
          <Alert
            showIcon
            type={sinistre.statut === 'SANS_SUITE' ? 'error' : 'warning'}
            style={{ borderRadius: 12, margin: '16px 0' }}
            message={t('details.alertes.horsPeriode.titre', {
              date: sinistre.dateAccident ? dayjs(sinistre.dateAccident).format('DD/MM/YYYY') : '?',
            debut: sinistre.dateEffet ? dayjs(sinistre.dateEffet).format('DD/MM/YYYY') : '?',
            fin: sinistre.dateEcheance ? dayjs(sinistre.dateEcheance).format('DD/MM/YYYY') : '?',
          })}
            description={sinistre.statut === 'SANS_SUITE'
              ? (sinistre.motifRejetGarantie || t('details.alertes.horsPeriode.sansSuite'))
              : t('details.alertes.horsPeriode.verifierDates')}
          />
        )}

        {/*  Bannière d'alerte délais.

          Allegee : le degrade, le carre d'icone de 40 px et le bouton plein colore
          en faisaient un troisieme pave concurrent du bloc maturite et de l'en-tete.
          Elle garde ce qui compte - le decompte, la couleur du niveau le plus grave,
          et le fait que toute la bande soit cliquable - dans une bande fine.

          La ligne de detail « X critiques · Y urgents » ne s'affiche plus que si
          deux niveaux au moins coexistent : sinon elle repetait le titre.  */}
        {nbAlertesTotal > 0 && (() => {
          const ton = nbCritiques > 0
            ? { accent: '#dc2626', fond: '#fef2f2', bord: '#fecaca', icone: <FireOutlined /> }
            : nbUrgents > 0
              ? { accent: '#ea580c', fond: '#fff7ed', bord: '#fed7aa', icone: <ExclamationCircleOutlined /> }
              : { accent: '#a16207', fond: '#fefce8', bord: '#fde68a', icone: <ClockCircleOutlined /> };

          const titre = nbCritiques > 0
            ? t('details.alertesDelais.titreCritiques', { count: nbCritiques })
            : nbUrgents > 0
              ? t('details.alertesDelais.titreUrgents', { count: nbUrgents }) : t('details.alertesDelais.titreAttention', { count: nbAttention });

          const parts = [
            nbCritiques > 0 ? t('details.alertesDelais.critiques', { count: nbCritiques }) : null,
            nbUrgents > 0 ? t('details.alertesDelais.urgents', { count: nbUrgents }) : null,
            nbAttention > 0 ? t('details.alertesDelais.enAttention', { n: nbAttention }) : null,
          ].filter(Boolean);

          return (
            <div
              role="button"
              tabIndex={0}
              onClick={() => setActiveTab('delais')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab('delais'); } }}
              style={{
                margin: '16px 0',
                padding: '10px 16px',
                borderRadius: 10,
                background: ton.fond,
                border: `1px solid ${ton.bord}`,
                display: 'flex', alignItems: 'center', gap: 12,
                cursor: 'pointer', flexWrap: 'wrap',
              }}
            >
              <span style={{ color: ton.accent, fontSize: 17, display: 'inline-flex' }}>{ton.icone}</span>
              <span style={{ fontSize: 13.5, fontWeight: 650, color: '#111827' }}>{titre}</span>
              {parts.length > 1 && (
                <span style={{ fontSize: 12.5, color: '#6b7280' }}>{parts.join(' · ')}</span>
              )}
              <span style={{ marginLeft: 'auto', color: ton.accent, fontWeight: 600, fontSize: 13 }}>
                {t('details.alertesDelais.voirRelancer')} <ArrowRightOutlined />
              </span>
            </div>
          );
        })()}

        {/*  Onglets  */}
        <div className="cb-card" style={{ padding: 0, overflow: 'hidden' }}>
          <Tabs
            style={{ margin: '20px 20px auto' }}
            activeKey={groupeDeLOnglet(activeTab)}
            onChange={(cleGroupe) => {
              // Changer de groupe ouvre son PREMIER onglet : un groupe qui s'ouvrirait
              // sur du vide en attendant un second clic ne servirait a rien.
              const g = GROUPES_ONGLETS.find((x) => x.cle === cleGroupe);
              if (g?.onglets?.length) changerOnglet(g.onglets[0]);
            }}
            tabBarStyle={{ padding: '0 24px', marginBottom: 0, borderBottom: '1px solid #e5e7eb' }}
            items={grouperOnglets([
              {
                key: 'infos', label: <span><FileTextOutlined /> {t('details.onglets.informations')}</span>,
                children: <div style={{ padding: 24 }}><InfoGenerales sinistre={sinistre} victimes={victimes} pv={pvActifDossier} /></div>,
              },
              // Onglet monté uniquement pour les dossiers issus de l'application : sur un
              // dossier back-office il serait structurellement vide (ni GPS, ni mémo, ni
              // usager déclarant), et un onglet toujours vide finit par ne plus être ouvert.
              //
              // L'origine se lit sur `canal`, désormais exposé par l'API : ni le statut
              // (SOUMISE vaut aussi pour le guichet), ni une sonde réseau.
              ...(estMobile ? [{
                key: 'declaration-mobile',
                label: <span><MobileOutlined /> {t('details.onglets.declarationMobile')}</span>,
                children: (
                  <OngletDeclarationMobile
                    sinistreTrackingId={sinistre.sinistreTrackingId}
                    statut={sinistre.statut}
                  />
                ),
              }] : []),
              {
                key: 'liaison',
                label: <span><LinkOutlined /> {t('details.onglets.lierDossiers')}</span>,
                children: (
                  <PanneauLiaison
                    sinistre={sinistre}
                    peutLier={can(HAB.SINISTRE_LIER)}
                    onChangement={loadSinistre}
                  />
                ),
              },
              // Onglet a part, et non section de l'onglet « Déclaration mobile » : c'est un
              // échange vivant, ouvert plusieurs fois par dossier, pas une donnée de
              // déclaration que l'on consulte une fois. L'enterrer sous un autre onglet le
              // rendrait invisible - or c'est le SEUL canal de dialogue avec l'usager.
              //
              // Même condition de montage que l'onglet déclaration : hors canal mobile il
              // n'y a personne au bout du fil. antd ne rend les children d'un onglet qu'à
              // sa première activation : le fil n'est donc pas chargé tant que l'agent ne
              // l'ouvre pas, et il ne faut pas forcer un montage anticipé - la lecture
              // marque les messages de l'usager comme lus côté serveur.
              ...(estMobile ? [{
                key: 'echanges-usager',
                label: <span><CommentOutlined /> {t('details.onglets.echangesAssure')}</span>,
                children: (
                  <div style={{ padding: 24 }}>
                    <FilDiscussionMobile
                      sinistreTrackingId={sinistre.sinistreTrackingId}
                      numeroSinistre={sinistre.numeroSinistreLocal}
                      hauteur={480}
                    />
                  </div>
                ),
              }] : []),
              {
                key: 'affectation', label: <span><UserSwitchOutlined /> {t('details.onglets.affectation')}</span>,
                children: (
                  <div style={{ padding: 24 }}>
                    <PanneauAffectation sinistre={sinistre} onChange={loadSinistre} />
                  </div>
                ),
              },
              {
                key: 'victimes', label: <span><UserOutlined /> {t('details.onglets.victimes')}</span>,
                children: <div style={{ padding: 24 }}><VictimesTab
                  sinistreId={id}
                  sinistre={sinistre}
                  nombreVictimesAttendu={sinistre?.nombreVictimesAttendu}
                  bilan={{
                    blesses: sinistre?.nombreBlessesAttendu,
                    decedes: sinistre?.nombreDecedesAttendu,
                    motif: sinistre?.motifModificationVictimes,
                  }}
                  onBilanUpdated={loadSinistre}
                  onDossierModifie={rafraichirSinistre}
                /></div>,
              },
              {
                key: 'pv', label: <span><FileTextOutlined /> {t('details.onglets.pv')}</span>,
                children: <div style={{ padding: 24 }}><PvTab sinistreId={id} onRowClick={setPvDetailId} sinistreStatut={sinistre.statut} /></div>,
              },
              {
                key: 'expertises',
                label: <span><ToolOutlined /> {t('details.onglets.expertises')}</span>,
                children: <OngletExpertises sinistre={sinistre} victimes={victimes} />
              },
              {
                key: 'delais',
                label: (
                  <span>
                    <ExclamationCircleOutlined /> {t('details.onglets.delaisPenalites')}
                    {nbCritiques > 0 && (
                      <span style={{
                        marginLeft: 6, background: '#dc2626', color: '#fff',
                        borderRadius: 10, fontSize: 10, padding: '1px 7px', fontWeight: 700,
                      }}>{nbCritiques}</span>
                    )}
                    {nbCritiques === 0 && nbUrgents > 0 && (
                      <span style={{
                        marginLeft: 6, background: '#ea580c', color: '#fff',
                        borderRadius: 10, fontSize: 10, padding: '1px 7px', fontWeight: 700,
                      }}>{nbUrgents}</span>
                    )}
                    {nbCritiques === 0 && nbUrgents === 0 && nbAttention > 0 && (
                      <span style={{
                        marginLeft: 6, background: '#a16207', color: '#fff',
                        borderRadius: 10, fontSize: 10, padding: '1px 7px', fontWeight: 700,
                      }}>{nbAttention}</span>
                    )}
                  </span>
                ),
                children: (
                  <div style={{ padding: 24 }}>
                    <OngletDelaisPenalites
                      sinistreTrackingId={id}
                      onRelanceEnvoyee={() => setJournalTick((t) => t + 1)}
                    />
                  </div>
                ),
              },
              {
                key: 'offres',
                label: (
                  <span>
                    <SafetyCertificateOutlined /> {t('details.onglets.offresIndemnisation')}
                    {victimes.filter(v => !v.estAdversaire).length > 0 && (
                      <span style={{
                        marginLeft: 6, background: '#1a6b3c', color: '#fff',
                        borderRadius: 10, fontSize: 10, padding: '1px 7px', fontWeight: 700,
                      }}>
                        {victimes.filter(v => !v.estAdversaire).length}
                      </span>
                    )}
                  </span>
                ),
                children: <OngletOffresIndemnisation sinistre={sinistre} victimes={victimes} onVictimesMaj={loadVictimes}
                  onTransformerEnReglement={(depuisOffre) => { setPrefillReglement(depuisOffre); setActiveTab('reglements'); }} />,
              },
              {
                key: 'encaissements', label: <span><DollarOutlined /> {t('details.onglets.encaissements')}</span>,
                children: <div style={{ padding: 24 }}><OngletEncaissements sinistre={sinistre} /></div>,
              },
              {
                key: 'prefinancements',
                label: <span><WalletOutlined /> {t('details.onglets.prefinancements')}</span>,
                children: <OngletPrefinancements sinistre={sinistre} />,
              },
              {
                key: 'reglements',
                label: <span><WalletOutlined /> {t('details.onglets.reglements')}</span>,
                children: <OngletReglements sinistre={sinistre} victimes={victimes}
                  prefill={prefillReglement} onPrefillConsomme={() => setPrefillReglement(null)} />
              },

              {
                key: 'courriers', label: <span><MailOutlined /> {t('details.onglets.courriers')}</span>,
                children: <div style={{ padding: 24 }}><CourriersTab sinistreId={id} /></div>,
              },
              {
                // Envoyé à / en attente de / reçu de la compagnie et du bureau homologue.
                key: 'echanges', label: <span><SendOutlined /> {t('details.onglets.echangesPartenaires')}</span>,
                children: <div style={{ padding: 24 }}><EchangesPartenairesPanel sinistre={sinistre} /></div>,
              },
              {
                key: 'documents', label: <span><FolderOpenOutlined /> {t('details.onglets.documents')}</span>,
                children: (
                  <div style={{ padding: 24 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: '#111827' }}>
                      <FileTextOutlined style={{ marginRight: 6 }} />{t('details.onglets.documentsOfficiels')}
                    </h3>
                    <OngletDocuments sinistre={sinistre} navigate={navigate} />
                    <div style={{
                      marginTop: 28, paddingTop: 20,
                      borderTop: '1px solid #e5e7eb',
                    }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: '#111827' }}>
                        <PaperClipOutlined style={{ marginRight: 6 }} />{t('details.onglets.piecesJointesGed')}
                      </h3>
                      <DocumentsTab sinistreId={id} navigate={navigate} />
                    </div>
                  </div>
                ),
              },
              {
                key: 'bilateraux', label: <span><UserOutlined /> {t('details.onglets.echangesBilateraux')}</span>,
                children: <OngletBilateraux sinistreTrackingId={id} />,
              },
              {
                key: 'journal', label: <span><AuditOutlined /> {t('details.onglets.historique')}{evenements.length > 0 ? ` (${evenements.length})` : ''}</span>,
                children: (
                  <div style={{ padding: 24 }}>
                    <JournalTab
                      evenements={evenements}
                      statutCourant={sinistre.statut}
                      redacteur={sinistre.redacteurNomPrenom}
                    />
                  </div>
                ),
              },
            ], activeTab, changerOnglet, t)}
          />
        </div>

        <NouveauPvModal
          open={pvCreateOpen}
          onClose={() => setPvCreateOpen(false)}
          onCreated={() => { setPvCreateOpen(false); setActiveTab('pv'); loadSinistre(); }}
          defaultSinistreId={id}
        />
        <ContentieuxModal
          open={contentieuxOpen}
          onClose={() => setContentieuxOpen(false)}
          onConfirm={onMettreEnContentieux}
          sinistre={sinistre}
          loading={savingLitige}
        />
        <ArbitrageModal
          open={arbitrageOpen}
          onClose={() => setArbitrageOpen(false)}
          onConfirm={onMettreEnArbitrage}
          sinistre={sinistre}
          loading={savingLitige}
        />
        <FicheHomologueModal
          open={ficheModalOpen}
          onClose={() => setFicheModalOpen(false)}
          onConfirm={onJoindreFicheHomologue}
          loading={savingFiche}
          dejaJointe={ficheHomologue === true}
          enregistrerDemande={ficheEnregistreDemande}
        />
        <ValidationModal
          open={validationModalOpen}
          onClose={() => setValidationModalOpen(false)}
          sinistre={sinistre}
          assure={assure}
          loading={savingValidation}
          onConfirm={onValiderDossier}
        />
        <DemandePvModal
          open={demandePvOpen}
          onClose={() => setDemandePvOpen(false)}
          sinistre={sinistre}
          /* `setDemandePvEnvoyee` vient de deploy : l'ecran retient que la demande
             est partie, pour ne pas la proposer deux fois. */
          onEnvoye={() => { setDemandePvEnvoyee(true); loadSinistre(); }}
        />
        <ConfirmationGarantieModal
          open={garantieModalOpen}
          onClose={() => setGarantieModalOpen(false)}
          sinistre={sinistre}
          loading={savingGarantie}
          onConfirm={onConfirmerGarantie}
          mode={(sinistre.statut === 'GARANTIE_REJETEE' || (sinistre.statut === 'SANS_SUITE' && sinistre.garantieAcquise === false)) ? 'recours' : 'confirmation'}
          onRejeter={['GARANTIE_DEMANDEE', 'GARANTIE_ATTENTE_CONFIRMATION'].includes(sinistre.statut)
            ? () => { setGarantieModalOpen(false); setRejeterModalOpen(true); }
            : undefined}
        />
        <RejeterGarantieModal
          open={rejeterModalOpen}
          onClose={() => setRejeterModalOpen(false)}
          sinistre={sinistre}
          loading={savingRejet}
          onConfirm={onRejeterGarantie}
        />
        <DetailPvModal
          open={Boolean(pvDetailId)}
          pvTrackingId={pvDetailId}
          onClose={() => setPvDetailId(null)}
          onUpdated={() => { loadPvs(); loadSinistre(); }}
        />
        <ChangerStatutModal
          open={statutModalOpen}
          sinistre={sinistre}
          sinistreId={id}
          onClose={() => setStatutModalOpen(false)}
          onChanged={loadSinistre}
        />
      </DiscussionDossier>
    </Layout>
  );
}
